import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { listEventAttendance } from "../lib/rangegridApi";

const ROLE_MODULES = [
  { id: "home", label: "Home", meta: "Today" },
  { id: "members", label: "Members", meta: "Roster" },
  { id: "programs", label: "Programs", meta: "Classes" },
  { id: "messages", label: "Messages", meta: "Drafts" },
  { id: "tasks", label: "Tasks", meta: "Follow-ups" },
  { id: "insights", label: "Insights", meta: "Trends" },
  { id: "settings", label: "Settings", meta: "Local" },
  { id: "range-board", label: "Range Board", meta: "Line status" },
  { id: "shooters", label: "Shooters", meta: "On line" },
  { id: "safety", label: "Safety", meta: "Incidents" },
  { id: "equipment", label: "Equipment", meta: "Ready" },
  { id: "shift-notes", label: "Shift Notes", meta: "Handoff" },
  { id: "issue-desk", label: "Issue Desk", meta: "Open issues" },
  { id: "service", label: "Service", meta: "Due work" },
  { id: "inventory", label: "Inventory", meta: "Assigned assets" },
  { id: "notes", label: "Notes", meta: "History" }
];

const INTERNAL_DEMO_EVENT_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|\btest\b|test event|audit)/i;

function programDisplayTitle(event = {}) {
  const title = String(event.title || "").trim();
  if (!title) return "Chapter class";
  if (!INTERNAL_DEMO_EVENT_COPY.test(title)) return title;

  const cleaned = title
    .replace(/^Demo\s+/i, "")
    .replace(/\bTest\b/gi, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?\s*$/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return /^(?:jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december|jan|january|feb|february|mar|march|apr|april|may)\s+\d/i.test(cleaned)
    ? "Chapter class"
    : cleaned || "Chapter class";
}

const ROLE_SUBTABS = {
  home: ["Today", "Attention", "Activity", "Handoff"],
  members: ["Roster", "Needs Review", "Requirements", "Notes"],
  programs: ["Upcoming", "Roster", "Attendance", "Prep"],
  messages: ["Compose", "Drafts", "Recent", "Templates"],
  tasks: ["Open", "Member Follow-Ups", "Program Prep", "Completed"],
  insights: ["Participation", "Growth", "Readiness", "Communication"],
  settings: ["Profile", "Lead Team", "Defaults"],
  "range-board": ["Line Status", "Cease-Fires", "Reservations", "Handoff"],
  shooters: ["Checked In", "Needs Attention", "Departed"],
  safety: ["Open", "Resolved", "Patterns"],
  equipment: ["Ready", "Needs Service", "Checked Out"],
  "shift-notes": ["Current Shift", "Handoff", "History"],
  "issue-desk": ["Open", "Malfunctions", "Member Reports", "Resolved"],
  service: ["Due Soon", "Round Counts", "Scheduled", "Completed"],
  inventory: ["Firearms", "Parts", "Availability", "Movement"],
  notes: ["Recent", "Service Notes", "Range Notes"]
};

const ROLE_LABELS = {
  admin: "Admin workspace",
  armorer: "Armorer workspace",
  chapter_leader: "Chapter lead workspace",
  event_staff: "Event team workspace",
  instructor: "Instructor workspace",
  owner: "Owner workspace",
  range_officer: "Range officer workspace",
  staff: "Team workspace"
};

const ROLE_WORKSPACE_CONFIGS = {
  owner: {
    moduleIds: ["home", "members", "programs", "messages", "tasks", "insights", "settings"],
    intro: "Owners can preview scoped team work while access grants, billing, delivery controls, and organization setup stay in the owner console."
  },
  admin: {
    moduleIds: ["home", "members", "programs", "messages", "tasks", "insights", "settings"],
    intro: "Admins can review scoped team work while owner-level access, setup, billing, and delivery controls stay in the owner console."
  },
  chapter_leader: {
    moduleIds: ["home", "members", "programs"],
    subtabs: {
      home: ["Today", "Attention"],
      members: ["Members"],
      programs: ["Upcoming", "Roster", "Attendance", "Signoff"]
    },
    intro: "Chapter members, classes, attendance, and signoff inside the assigned scope."
  },
  instructor: {
    moduleIds: ["home", "programs", "tasks", "insights"],
    subtabs: {
      home: ["Today", "Attention", "Handoff"],
      programs: ["Upcoming", "Roster", "Attendance", "Prep"],
      tasks: ["Open", "Program Prep", "Completed"],
      insights: ["Participation", "Readiness"]
    },
    intro: "Instructors get the programs, rosters, attendance, prep work, and readiness signals they are assigned to run."
  },
  staff: {
    moduleIds: ["home", "programs", "tasks"],
    subtabs: {
      home: ["Today", "Attention", "Handoff"],
      programs: ["Upcoming", "Roster", "Attendance", "Prep"],
      tasks: ["Open", "Program Prep", "Completed"]
    },
    intro: "Team members get day-of program work, assigned rosters, attendance, and task handoff without owner-console controls."
  },
  event_staff: {
    moduleIds: ["home", "programs", "tasks"],
    subtabs: {
      home: ["Today", "Attention", "Handoff"],
      programs: ["Upcoming", "Roster", "Attendance", "Prep"],
      tasks: ["Open", "Program Prep", "Completed"]
    },
    intro: "Event team members get check-in, waitlist, attendance, and prep work for the programs they are assigned to support."
  },
  range_officer: {
    moduleIds: ["home", "range-board", "shooters", "safety", "equipment", "shift-notes"],
    subtabs: {
      home: ["Today", "Attention", "Handoff"],
      "range-board": ["Line Status", "Cease-Fires", "Reservations", "Handoff"],
      shooters: ["Checked In", "Needs Attention", "Departed"],
      safety: ["Open", "Resolved", "Patterns"],
      equipment: ["Ready", "Needs Service", "Checked Out"],
      "shift-notes": ["Current Shift", "Handoff", "History"]
    },
    intro: "Range officers get line status, checked-in shooters, safety follow-up, equipment readiness, and shift handoff inside their assigned scope."
  },
  armorer: {
    moduleIds: ["home", "issue-desk", "service", "inventory", "insights", "notes"],
    subtabs: {
      home: ["Today", "Attention", "Handoff"],
      "issue-desk": ["Open", "Malfunctions", "Member Reports", "Resolved"],
      service: ["Due Soon", "Round Counts", "Scheduled", "Completed"],
      inventory: ["Firearms", "Parts", "Availability", "Movement"],
      insights: ["Readiness"],
      notes: ["Recent", "Service Notes", "Range Notes"]
    },
    intro: "Armorers get issue intake, service work, inventory readiness, and firearm notes inside their assigned armory scope."
  }
};

const ROLE_MODULE_APPLICATION_SCOPES = {
  equipment: ["daily-ops", "resources"],
  insights: ["armory", "reports", "staff-portal"],
  inventory: ["armory", "resources"],
  "issue-desk": ["armory", "resources"],
  members: ["membership"],
  messages: ["communications", "notifications", "staff-portal"],
  notes: ["armory", "resources"],
  programs: ["events"],
  "range-board": ["daily-ops"],
  safety: ["daily-ops"],
  service: ["armory", "resources"],
  settings: ["chapters", "range-setup"],
  "shift-notes": ["daily-ops"],
  shooters: ["daily-ops"],
  tasks: ["armory", "chapters", "daily-ops", "events", "membership", "resources"]
};

const ROLE_HOME_SURFACES = {
  armorer: {
    today: {
      title: "Start with armory readiness",
      body: "Review new issues, service work, assigned inventory, and notes for the armory scope before equipment returns to use.",
      action: "Open Issue Desk",
      moduleId: "issue-desk",
      subtab: "open",
      facts: [
        { label: "Issue intake", value: "Issue Desk" },
        { label: "Service work", value: "Service" },
        { label: "Inventory", value: "Availability" }
      ]
    },
    attention: [
      {
        key: "armory-issues",
        type: "Issue intake",
        title: "Triage open firearm and equipment concerns",
        detail: "Malfunctions, member reports, and checkout concerns belong in the issue desk.",
        due: "Before issue",
        action: "Open Issue Desk",
        moduleId: "issue-desk",
        subtab: "open"
      },
      {
        key: "armory-service",
        type: "Service",
        title: "Review due service and round-count signals",
        detail: "Date-based and round-based service work stays in Service.",
        due: "Before return",
        action: "Open Service",
        moduleId: "service",
        subtab: "due-soon"
      },
      {
        key: "armory-inventory",
        type: "Inventory",
        title: "Confirm what can be issued",
        detail: "Availability, movement, and blocked assets stay in Inventory.",
        due: "Before checkout",
        action: "Open Inventory",
        moduleId: "inventory",
        subtab: "availability"
      }
    ],
    handoff: [
      {
        key: "armory-next",
        label: "Next work",
        value: "Issue intake first",
        detail: "Review open issues before service scheduling or inventory release."
      },
      {
        key: "armory-boundary",
        label: "Owner boundary",
        value: "No access or policy changes",
        detail: "Role grants, app setup, and inventory policy remain in owner apps."
      },
      {
        key: "armory-history",
        label: "History",
        value: "Notes stay with readiness",
        detail: "Service and range-floor notes stay close to the armory workflow."
      }
    ],
    commandFacts: [
      { label: "Issues", value: "Desk" },
      { label: "Service", value: "Due work" },
      { label: "Inventory", value: "Assigned" },
      { label: "Scope", value: "Armory" }
    ]
  },
  range_officer: {
    today: {
      title: "Start with the range board",
      body: "Review line status, shooters needing attention, safety follow-up, equipment readiness, and shift notes for the assigned operating scope.",
      action: "Open Range Board",
      moduleId: "range-board",
      subtab: "line-status",
      facts: [
        { label: "Line status", value: "Range Board" },
        { label: "Shooters", value: "On line" },
        { label: "Handoff", value: "Shift Notes" }
      ]
    },
    attention: [
      {
        key: "range-line",
        type: "Line status",
        title: "Confirm open lines and cease-fire state",
        detail: "Line control and reservations belong in Range Board.",
        due: "Now",
        action: "Open Range Board",
        moduleId: "range-board",
        subtab: "line-status"
      },
      {
        key: "range-shooters",
        type: "Shooters",
        title: "Review shooters needing attention",
        detail: "Qualification gaps, check-in issues, and line concerns stay in Shooters.",
        due: "During shift",
        action: "Open Shooters",
        moduleId: "shooters",
        subtab: "needs-attention"
      },
      {
        key: "range-safety",
        type: "Safety",
        title: "Keep safety follow-up visible",
        detail: "Open and resolved safety notes stay in Safety, not the owner audit log.",
        due: "Before handoff",
        action: "Open Safety",
        moduleId: "safety",
        subtab: "open"
      }
    ],
    handoff: [
      {
        key: "range-next",
        label: "Next work",
        value: "Line status first",
        detail: "Confirm the range board before shooter flow or equipment decisions."
      },
      {
        key: "range-boundary",
        label: "Owner boundary",
        value: "No policy setup",
        detail: "Operating rules, access grants, and facility setup remain in owner apps."
      },
      {
        key: "range-notes",
        label: "Shift context",
        value: "Use Shift Notes",
        detail: "Current-shift notes and handoff stay together for the next officer."
      }
    ],
    commandFacts: [
      { label: "Line", value: "Board" },
      { label: "Shooters", value: "On line" },
      { label: "Safety", value: "Open" },
      { label: "Scope", value: "Shift" }
    ]
  }
};

function chapterTimezone(selectedRange = {}, chapter = {}) {
  const label = [chapter?.name, chapter?.city, chapter?.region, chapter?.state].filter(Boolean).join(" ").toLowerCase();
  if (chapter?.timezone && chapter.timezone !== "America/Los_Angeles") {
    return chapter.timezone;
  }
  if (/(saint paul|st\.?\s*paul|twin cities|minneapolis|minnesota|\bmn\b)/i.test(label)) {
    return "America/Chicago";
  }
  return chapter?.timezone || selectedRange?.timezone || "Timezone not set";
}

function formatDateTime(value, timezone = "") {
  if (!value) {
    return "Time not set";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timezone && timezone !== "Timezone not set" ? { timeZone: timezone, timeZoneName: "short" } : {})
  }).format(date);
}

function formatDateOnly(value, timezone = "") {
  if (!value) {
    return "Not recorded";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(timezone && timezone !== "Timezone not set" ? { timeZone: timezone } : {})
  }).format(date);
}

function monthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(date = new Date()) {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
}

function eventDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function compactCalendarTimeParts(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour = hours % 12 || 12;
  return {
    label: minutes ? `${hour}:${String(minutes).padStart(2, "0")}` : String(hour),
    period: hours >= 12 ? "PM" : "AM"
  };
}

function eventTimeRange(event = {}) {
  const start = compactCalendarTimeParts(event.startsAt);
  const end = compactCalendarTimeParts(event.endsAt);
  if (!start && !end) {
    return "";
  }
  if (start && end && start.period === end.period) {
    return `${start.label}-${end.label} ${end.period}`;
  }
  if (start && end) {
    return `${start.label} ${start.period}-${end.label} ${end.period}`;
  }
  const only = start || end;
  return `${only.label} ${only.period}`;
}

function scheduleStatus(event = {}, now = new Date()) {
  if (event.status === "pending_approval" || event.approval?.status === "pending") {
    return "Needs approval";
  }
  const start = Date.parse(event.startsAt || "");
  const end = Date.parse(event.endsAt || event.startsAt || "");
  const nowTime = now.getTime();
  if (Number.isFinite(start) && Number.isFinite(end) && start <= nowTime && nowTime <= end) {
    return "In progress";
  }
  if (Number.isFinite(end) && end < nowTime) {
    return "Past";
  }
  return "Future";
}

function buildMonthDays(monthDate = new Date()) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function formatChapter(event, chapters) {
  if (event?.chapterSnapshot?.name) {
    return event.chapterSnapshot.name;
  }
  const ids = eventChapterIds(event);
  if (ids.length === 0) {
    return "All chapters";
  }
  const names = ids
    .map((chapterId) => chapters.find((chapter) => chapter.chapterId === chapterId)?.name)
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "Selected chapters";
}

function eventChapterIds(event) {
  return [
    event?.chapterId,
    ...(Array.isArray(event?.chapterIds) ? event.chapterIds : [])
  ].filter(Boolean);
}

function accessChapterIds(selectedRange = {}) {
  const access = selectedRange?.access || {};
  return [
    ...(Array.isArray(access.chapterIds) ? access.chapterIds : []),
    ...(Array.isArray(access.scope?.chapterIds) ? access.scope.chapterIds : [])
  ]
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean)
    .filter((chapterId, index, list) => list.indexOf(chapterId) === index);
}

function accessApplicationScopes(selectedRange = {}) {
  const access = selectedRange?.access || {};
  return [
    ...(Array.isArray(access.applicationScopes) ? access.applicationScopes : []),
    ...(Array.isArray(access.scope?.applicationScopes) ? access.scope.applicationScopes : [])
  ]
    .map((scope) => String(scope || "").trim())
    .filter(Boolean)
    .filter((scope, index, list) => list.indexOf(scope) === index);
}

function moduleAllowedByApplicationScope(moduleId, selectedRange = {}) {
  if (moduleId === "home") {
    return true;
  }

  const role = effectiveWorkspaceRole(selectedRange);
  if (role === "owner" || role === "admin") {
    return true;
  }

  const scopes = accessApplicationScopes(selectedRange);
  if (scopes.length === 0) {
    return true;
  }

  const allowedScopes = ROLE_MODULE_APPLICATION_SCOPES[moduleId] || [];
  return allowedScopes.some((scope) => scopes.includes(scope));
}

function chapterScopeLabel(selectedRange = {}, chapters = [], selectedChapterId = "") {
  if (selectedChapterId) {
    return chapters.find((chapter) => chapter.chapterId === selectedChapterId)?.name || "Selected chapter";
  }

  const scopeIds = accessChapterIds(selectedRange);
  const scopeMode = String(selectedRange?.access?.scopeMode || "").toLowerCase();
  const hasLimitedScope = scopeMode === "limited" || scopeIds.length > 0;
  if (hasLimitedScope && scopeIds.length > 0) {
    const names = scopeIds
      .map((chapterId) => chapters.find((chapter) => chapter.chapterId === chapterId)?.name)
      .filter(Boolean);

    if (names.length === 1) {
      return names[0];
    }
    if (names.length === 2) {
      return names.join(", ");
    }
    if (names.length > 2) {
      return `${names[0]}, ${names[1]} + ${names.length - 2} more`;
    }
    return scopeIds.length === 1 ? "Assigned chapter" : `${scopeIds.length} assigned chapters`;
  }

  if (chapters.length === 1) {
    return chapters[0].name;
  }
  return chapters.length > 0 ? "All visible chapters" : "Assigned workspace";
}

function chapterLocation(chapter = {}) {
  return [chapter.city, chapter.region, chapter.state].filter(Boolean).join(", ") || "Location not set";
}

function chapterLeaders(chapter = {}) {
  return [
    ...(Array.isArray(chapter.leaderEmails) ? chapter.leaderEmails : []),
    ...(Array.isArray(chapter.leaders) ? chapter.leaders.map((leader) => leader.email || leader.fullName || leader.name) : [])
  ]
    .map((leader) => String(leader || "").trim())
    .filter(Boolean)
    .filter((leader, index, list) => list.indexOf(leader) === index);
}

function chapterLeaderLabel(chapter = {}) {
  const leaders = chapterLeaders(chapter);
  if (leaders.length === 0) {
    return "No lead contact listed";
  }
  if (leaders.length === 1) {
    return leaders[0];
  }
  return `${leaders[0]} + ${leaders.length - 1} more`;
}

function chapterPublicState(chapter = {}) {
  if (chapter.publicVisible === false) {
    return "Hidden from public directory";
  }
  return "Visible in public directory";
}

function statusLabel(value) {
  return String(value || "unknown")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusClass(value) {
  const normalized = String(value || "").toLowerCase();
  if (["confirmed", "attended", "paid", "not_required", "published"].includes(normalized)) {
    return "is-positive";
  }
  if (["waitlisted", "pending", "checkout_started", "draft", "excused"].includes(normalized)) {
    return "is-warning";
  }
  if (["cancelled", "no_show", "failed"].includes(normalized)) {
    return "is-danger";
  }
  return "";
}

function eventRosterCount(event) {
  const registered = event?.registrationSummary?.registered ?? event?.registrationCounts?.registered;
  const confirmed = event?.registrationSummary?.confirmed ?? event?.registrationCounts?.confirmed;
  const capacity = event?.capacity?.maxAttendees;
  if (Number.isFinite(Number(confirmed)) && Number.isFinite(Number(capacity)) && Number(capacity) > 0) {
    return `${confirmed}/${capacity} confirmed`;
  }
  if (Number.isFinite(Number(registered))) {
    return `${registered} registered`;
  }
  return "Roster pending";
}

function programCapacityLabel(event, confirmedCount = 0) {
  const capacity = Number(event?.capacity?.maxAttendees || 0);
  if (capacity > 0) {
    return `${confirmedCount}/${capacity} confirmed`;
  }
  return "Capacity not listed";
}

function programInstructorLabel(event = {}) {
  const instructors = Array.isArray(event.instructors) ? event.instructors : [];
  const names = instructors
    .map((instructor) => instructor.name || instructor.fullName || instructor.email)
    .filter(Boolean);
  if (names.length === 0) {
    return "Instructor coverage not listed";
  }
  if (names.length === 1) {
    return INTERNAL_DEMO_EVENT_COPY.test(names[0]) ? "Chapter instructor" : names[0];
  }
  return `${INTERNAL_DEMO_EVENT_COPY.test(names[0]) ? "Chapter instructor" : names[0]} + ${names.length - 1} more`;
}

function programRegistrationWindowLabel(event = {}, timezone = "") {
  const closesAt = event.registrationPolicy?.closesAt;
  const opensAt = event.registrationPolicy?.opensAt;
  if (closesAt) {
    return `Registration closes ${formatDateTime(closesAt, timezone)}`;
  }
  if (opensAt) {
    return `Registration opens ${formatDateTime(opensAt, timezone)}`;
  }
  return "Registration window not listed";
}

function selectedClassAttendanceSummary({
  attended = 0,
  noShow = 0,
  open = 0,
  roster = 0,
  waitlisted = 0
} = {}) {
  if (roster <= 0) {
    return "No registrations yet.";
  }
  const parts = [
    `${attended} attended`,
    `${open} open`,
    noShow > 0 ? `${noShow} no-show` : "",
    waitlisted > 0 ? `${waitlisted} waitlisted` : ""
  ].filter(Boolean);
  return parts.join(" / ");
}

function roleSubtabKey(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function displayRoleSubtabLabel(moduleId, label) {
  if (moduleId === "programs" && roleSubtabKey(label) === "upcoming") {
    return "Classes";
  }
  return label;
}

function effectiveWorkspaceRole(selectedRange = {}, roleOverride = "") {
  const role = roleOverride || selectedRange?.access?.role || selectedRange?.access?.accessRole || "staff";
  return String(role || "staff").toLowerCase().replace(/[-\s]+/g, "_");
}

function roleWorkspaceLabel(selectedRange = {}, roleOverride = "") {
  const role = effectiveWorkspaceRole(selectedRange, roleOverride);
  return ROLE_LABELS[role] || "Team workspace";
}

function roleWorkspaceConfig(selectedRange = {}, roleOverride = "") {
  const role = effectiveWorkspaceRole(selectedRange, roleOverride);
  return ROLE_WORKSPACE_CONFIGS[role] || ROLE_WORKSPACE_CONFIGS.staff;
}

function roleModuleConfig(module, role) {
  if (role !== "chapter_leader") {
    return module;
  }
  const overrides = {
    home: { label: "Home", meta: "Today" },
    members: { label: "Members", meta: "Members" },
    programs: { label: "Programs & Events", meta: "Attendance" }
  };
  return {
    ...module,
    ...(overrides[module.id] || {})
  };
}

function workspaceAppSlugForModule(moduleId) {
  if (moduleId === "members") {
    return "members";
  }
  if (moduleId === "programs") {
    return "events";
  }
  return "home";
}

const OPERATOR_MODULE_COPY = {
  "range-board": {
    "line-status": ["Line status", "Use this tab for bay state, open lines, cease-fire state, reservations, and the next operating action for this officer's assigned shift."],
    "cease-fires": ["Cease-fire control", "Cease-fire notes and reopen checks belong here. Owner-level operating rules stay in Range Setup and Range Operations."],
    reservations: ["Reservation context", "Assigned reservations and lane pressure stay here without exposing the full facility calendar."],
    handoff: ["Range board handoff", "Use this for the next officer's operating context: what is open, what is blocked, and what still needs eyes."]
  },
  shooters: {
    "checked-in": ["Checked-in shooters", "Use this tab for shooters currently on the line, their lane or bay context, and any range-managed attention item."],
    "needs-attention": ["Needs attention", "Safety flags, qualification gaps, and check-in issues stay here without turning into the full member database."],
    departed: ["Departed shooters", "Recent departures stay visible long enough for shift review, cleanup, and incident follow-up."]
  },
  safety: {
    open: ["Open safety follow-up", "Active safety notes and incident follow-up belong here. Compliance setup and global policy stay in owner workflows."],
    resolved: ["Resolved safety items", "Recently resolved items stay available for shift context without becoming the organization-wide audit log."],
    patterns: ["Safety patterns", "Repeated range-floor signals stay grouped here for shift review and owner handoff."]
  },
  equipment: {
    ready: ["Equipment ready", "Check line equipment, rental readiness, and assigned range assets for the current operating scope."],
    "needs-service": ["Needs service", "Equipment that should be pulled from use or reviewed by an armorer stays visible here."],
    "checked-out": ["Checked out", "Current checkouts stay here without exposing the full armory or facility inventory ledger."]
  },
  "shift-notes": {
    "current-shift": ["Current shift notes", "Use this for same-day operating notes that help the range officer keep the line moving."],
    handoff: ["Officer handoff", "End-of-shift handoff belongs here so the next officer sees the few things that still matter."],
    history: ["Shift note history", "Recent shift history can stay scoped to the officer's assignment rather than the full audit trail."]
  },
  "issue-desk": {
    open: ["Open issue desk", "Armory issue intake belongs here: malfunctions, checkout concerns, missing context, and service triage."],
    malfunctions: ["Malfunctions", "Firearm malfunctions stay grouped here with the pattern, round-count context, and next action."],
    "member-reports": ["Member reports", "Member-submitted firearm or equipment concerns are reviewed here before becoming service work."],
    resolved: ["Resolved issues", "Recently resolved issue history stays close enough for the armorer to spot repeats."]
  },
  service: {
    "due-soon": ["Service due soon", "Date-based, round-count-based, and manual service work converge here for armory readiness."],
    "round-counts": ["Round counts", "Round-count tracking belongs here so service intervals are driven by use, not only calendar dates."],
    scheduled: ["Scheduled service", "Planned service work stays here without duplicating the full armory inventory surface."],
    completed: ["Completed service", "Recent service completions stay visible for readiness and repeat-issue review."]
  },
  inventory: {
    firearms: ["Firearms", "Assigned firearms stay here with readiness, service state, and checkout availability."],
    parts: ["Parts", "Parts and consumables stay scoped here for service and checkout readiness."],
    availability: ["Availability", "Availability signals should answer what can be issued, what is blocked, and what needs service."],
    movement: ["Movement", "Recent checkout, return, service, and storage movement stays summarized here."]
  },
  notes: {
    recent: ["Recent notes", "Recent armory notes belong here when they help explain service, readiness, or handoff."],
    "service-notes": ["Service notes", "Detailed service observations stay grouped here without exposing raw maintenance records."],
    "range-notes": ["Range notes", "Range-floor notes that affect armory readiness stay here for follow-up."]
  }
};

function operatorModulePanel(moduleId, activeSubtab, facts = []) {
  const copy = OPERATOR_MODULE_COPY[moduleId]?.[activeSubtab];
  if (!copy) {
    return null;
  }
  return (
    <div className="role-workspace-dashboard-grid">
      <section className="role-workspace-list-panel">
        <div>
          <span className="section-kicker">Assigned work</span>
          <h3>{copy[0]}</h3>
          <p>{copy[1]}</p>
        </div>
      </section>
      <aside className="role-workspace-side-panel">
        <span className="section-kicker">Context</span>
        <div className="role-workspace-preview-list">
          {facts.map((fact) => (
            <div key={fact.label}>
              <span>{fact.label}</span>
              <strong>{fact.value}</strong>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function programBrief(event, chapters, timezone = "") {
  if (!event) {
    return "";
  }
  return [
    formatDateTime(event.startsAt, timezone),
    event.location || "Location not set",
    formatChapter(event, chapters)
  ].filter(Boolean).join(" / ");
}

function recipientCountLabel(count, fallback = "No people in this audience yet") {
  const numericCount = Number(count || 0);
  if (numericCount <= 0) {
    return fallback;
  }
  if (numericCount === 1) {
    return "1 recipient";
  }
  return `${numericCount} recipients`;
}

function memberName(member = {}) {
  return member.fullName || member.email || "Member";
}

function registrationDisplayName(registration = {}) {
  return registration.memberSnapshot?.fullName ||
    registration.member?.fullName ||
    registration.fullName ||
    registration.email ||
    "Participant";
}

function registrationEmail(registration = {}) {
  return registration.email || registration.memberSnapshot?.email || registration.member?.email || "";
}

function filterAwareProgramEmptyCopy(filters = {}, selectedChapterLabel = "this chapter") {
  const query = String(filters.q || "").trim();
  const status = String(filters.status || "").trim();
  if (query) {
    return `No classes match "${query}" in ${selectedChapterLabel}.`;
  }
  if (status) {
    return `No ${statusLabel(status).toLowerCase()} classes are visible in ${selectedChapterLabel}.`;
  }
  return "Published or completed classes assigned to this chapter appear here.";
}

function draftTemplateForProgram(event, chapters = [], timezone = "") {
  return {
    subject: `${programDisplayTitle(event)} reminder`,
    body: `Hi everyone, this is a reminder for ${programDisplayTitle(event)} at ${programBrief(event, chapters, timezone)}. Reply with any questions before the program.`,
    audience: event?.eventId || "chapter"
  };
}

function memberChapterIds(member = {}) {
  return [
    member.homeChapterId,
    member.chapterId,
    member.chapterSnapshot?.chapterId,
    ...(Array.isArray(member.chapterIds) ? member.chapterIds : [])
  ]
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean)
    .filter((chapterId, index, list) => list.indexOf(chapterId) === index);
}

function memberChapterLabel(member = {}, chapters = []) {
  if (member.chapterSnapshot?.name) {
    return member.chapterSnapshot.name;
  }
  const ids = memberChapterIds(member);
  if (ids.length === 0) {
    return "No chapter set";
  }
  const names = ids
    .map((chapterId) => chapters.find((chapter) => chapter.chapterId === chapterId)?.name)
    .filter(Boolean);
  if (names.length > 0) {
    return names.join(", ");
  }
  return ids.length === 1 ? "Assigned chapter" : `${ids.length} assigned chapters`;
}

function membershipTypeName(membershipTypeId, membershipTypes = []) {
  if (!membershipTypeId) {
    return "No plan";
  }
  return membershipTypes.find((type) => type.membershipTypeId === membershipTypeId)?.name || "Plan needs review";
}

function visibleText(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "";
  }
  if (/^(unavailable|not recorded|email unavailable|phone unavailable)$/i.test(text)) {
    return "";
  }
  return text;
}

function memberAddressLabel(member = {}) {
  const address = member.address || member.profile?.address || {};
  const line1 = visibleText(address.line1 || address.street || address.street1);
  const line2 = visibleText(address.line2 || address.unit || address.street2);
  const cityLine = [
    address.city,
    address.state || address.region,
    address.postalCode || address.zip
  ].map(visibleText).filter(Boolean).join(", ");
  const country = visibleText(address.country);
  const parts = [line1, line2, cityLine, country].filter(Boolean);
  return parts.length > 0 ? parts.join(" / ") : "";
}

function chapterLeadMemberProfileRows(member = {}, journey = {}, membershipTypes = [], chapters = [], timezone = "", visibility = {}) {
  const rows = [
    { label: "Chapter", value: memberChapterLabel(member, chapters), always: true }
  ];
  const email = visibleText(member.email);
  if (email) {
    rows.push({ label: "Email", value: email, always: true });
  }
  const phone = visibleText(member.phone || member.profile?.phone);
  if (visibility.phone && phone) {
    rows.push({ label: "Phone", value: phone });
  }
  const address = memberAddressLabel(member);
  if (visibility.address && address) {
    rows.push({ label: "Address", value: address, wide: true });
  }
  if (visibility.memberNumber && member.memberNumber) {
    rows.push({ label: "Member number", value: visibleText(member.memberNumber) });
  }
  if (visibility.membershipStatus && member.membershipTypeId) {
    const planName = membershipTypeName(member.membershipTypeId, membershipTypes);
    if (!/^(no plan|plan needs review)$/i.test(planName)) {
      rows.push({ label: "Membership", value: planName });
    }
  }
  if (visibility.joinDate && journey?.joinedAt && memberJoinTimestamp(member) > 0) {
    rows.push({ label: "Joined", value: formatDateOnly(journey.joinedAt, timezone) });
  }
  rows.push({ label: "Classes attended", value: journey?.attendedClasses?.length || 0 });
  if (journey?.lastClass?.startsAt) {
    rows.push({ label: "Last class", value: formatDateOnly(journey.lastClass.startsAt, timezone) });
  }
  return rows.filter((row) => row.always || visibleText(row.value) || row.value === 0);
}

function memberAccessLabel(member = {}) {
  return statusLabel(member.expiration?.effectiveStatus || member.status || "unknown");
}

function memberJoinTimestamp(member = {}) {
  const value = Date.parse(member.joinedAt || member.memberSince || member.membership?.currentTermStart || member.createdAt || member.startDate || "");
  return Number.isFinite(value) ? value : 0;
}

function memberJoinDate(member = {}) {
  const timestamp = memberJoinTimestamp(member);
  return timestamp ? new Date(timestamp).toISOString() : "";
}

function memberRequirement(member = {}, key) {
  return member.review?.requirements?.[key] || {};
}

function memberRequirementOpen(member = {}, key) {
  const requirement = memberRequirement(member, key);
  if (!requirement.status && requirement.required !== true) {
    return false;
  }
  return requirement.required !== false && requirement.status !== "completed" && requirement.status !== "approved";
}

function memberNeedsReview(member = {}) {
  const reviewStatus = member.reviewSummary?.reviewStatus || member.review?.status;
  return (
    member.status === "pending" ||
    member.planChangeRequest?.status === "pending" ||
    ["pending", "needs_info", "rejected"].includes(reviewStatus) ||
    memberRequirementOpen(member, "waiver") ||
    memberRequirementOpen(member, "prequalification") ||
    memberRequirementOpen(member, "backgroundCheck")
  );
}

function memberEngagementLabel(member = {}) {
  if (member.lastSeenAt || member.lastVisitAt) {
    return `Last seen ${formatDateTime(member.lastSeenAt || member.lastVisitAt)}`;
  }
  const joinedAt = memberJoinDate(member);
  if (joinedAt) {
    return `Joined ${formatDateOnly(joinedAt)}`;
  }
  return "Member record";
}

function messageBoundaryCopy(activeSubtab) {
  const copy = {
    compose: "Draft local chapter or program updates here. Delivery approval, resend history, provider setup, and organization-wide broadcasts stay in Communications and Notifications.",
    drafts: "Keep local working drafts close to the chapter lead. Sent delivery state and archive decisions stay in Notifications.",
    recent: "Show recent chapter communication context without exposing the notification delivery machine.",
    templates: "Use local templates for recurring chapter updates. Organization templates and delivery rules stay owner-controlled."
  };
  return copy[activeSubtab] || copy.compose;
}

function taskBoundaryCopy(activeSubtab) {
  const copy = {
    open: "This is the lead's current work queue: attendance gaps, waitlist decisions, payment follow-up, and near-term program prep.",
    "member-follow-ups": "Member follow-ups stay focused on people who need a chapter lead decision. Plan setup and billing controls stay in organization admin tools.",
    "program-prep": "Program prep is for readiness before the event. Templates, prices, and organization-wide program setup stay in Programs & Events.",
    completed: "Completed work gives handoff context without becoming an audit export."
  };
  return copy[activeSubtab] || copy.open;
}

function safePercent(numerator, denominator) {
  const top = Number(numerator || 0);
  const bottom = Number(denominator || 0);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round((top / bottom) * 100)));
}

function insightBar(label, value, maxValue, caption, tone = "") {
  const max = Math.max(Number(maxValue || 0), Number(value || 0), 1);
  const width = safePercent(value, max);
  return (
    <div className={`role-workspace-insight-bar ${tone}`}>
      <div>
        <strong>{label}</strong>
        <span>{caption}</span>
      </div>
      <em>{value}</em>
      <span className="role-workspace-insight-track" aria-hidden="true">
        <span style={{ width: `${width}%` }} />
      </span>
    </div>
  );
}

function insightPercentCard(label, numerator, denominator, caption) {
  const percent = safePercent(numerator, denominator);
  return (
    <div>
      <span>{label}</span>
      <strong>{denominator > 0 ? `${percent}%` : "No data"}</strong>
      <small>{caption}</small>
    </div>
  );
}

function selectedEventFilters(events, filters) {
  const now = Date.now();
  return events.filter((event) => {
    const status = String(event.status || "").toLowerCase();
    const endsAt = Date.parse(event.endsAt || event.startsAt || "");
    const isPast = Number.isFinite(endsAt) && endsAt < now;
    const isUpcoming = status === "published" && !isPast;
    const isCompleted = status === "completed" || isPast;
    if (filters.status === "upcoming" && !isUpcoming) {
      return false;
    }
    if (filters.status === "completed" && !isCompleted) {
      return false;
    }
    if (filters.status && !["upcoming", "completed"].includes(filters.status) && status !== filters.status) {
      return false;
    }
    if (filters.chapterId && !eventChapterIds(event).includes(filters.chapterId)) {
      return false;
    }
    if (filters.q) {
      const needle = filters.q.trim().toLowerCase();
      const haystack = [
        event.title,
        event.location,
        event.type,
        event.chapterSnapshot?.name
      ].join(" ").toLowerCase();
      if (!haystack.includes(needle)) {
        return false;
      }
    }
    return filters.status === "completed" || !Number.isFinite(endsAt) || endsAt >= now || status === "completed";
  });
}

function staffHashParts(hash = "") {
  const rawHash = String(hash || "").replace(/^#staff-(?:module-)?/, "");
  if (!rawHash) {
    return { moduleId: "", subtabId: "" };
  }
  const moduleId = ROLE_MODULES
    .map((module) => module.id)
    .sort((left, right) => right.length - left.length)
    .find((id) => rawHash === id || rawHash.startsWith(`${id}-`)) || "";
  return {
    moduleId,
    subtabId: moduleId && rawHash !== moduleId ? rawHash.slice(moduleId.length + 1) : ""
  };
}

export function StaffPortalPage({
  chapters = [],
  eventAttendance = [],
  eventAttendanceLoading = false,
  eventAttendanceSummary,
  eventFilters,
  eventRegistrations = [],
  eventRegistrationsLoading = false,
  eventRegistrationsSummary,
  events = [],
  loading = false,
  members = [],
  membersLoading = false,
  membershipSummary,
  notifications = [],
  notificationsLoading = false,
  onRecordEventAttendance,
  onSignOffEvent,
  onUpdateEventAttendance,
  onUpdateEventRegistration,
  recordingAttendanceKey = "",
  selectedEventId,
  selectedRange,
  setSelectedEventId,
  updatingEventId = "",
  updatingEventRegistrationKey = "",
  workspaceAppBasePath = "",
  workspaceAppModule = "",
  workspaceRoleOverride = ""
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialStaffHash = typeof window !== "undefined" ? staffHashParts(window.location.hash) : { moduleId: "", subtabId: "" };
  const initialWorkspaceRole = effectiveWorkspaceRole(selectedRange, workspaceRoleOverride);
  const [activeModule, setActiveModule] = useState(workspaceAppModule || initialStaffHash.moduleId || "home");
  const [activeSubtabs, setActiveSubtabs] = useState({
    home: "today",
    members: initialWorkspaceRole === "chapter_leader" ? "members" : "roster",
    programs: initialStaffHash.moduleId === "programs" && initialStaffHash.subtabId ? initialStaffHash.subtabId : "upcoming",
    messages: "compose",
    tasks: "open",
    insights: "participation",
    settings: initialStaffHash.moduleId === "settings" && initialStaffHash.subtabId ? initialStaffHash.subtabId : "profile",
    ...(initialStaffHash.moduleId && initialStaffHash.subtabId ? { [initialStaffHash.moduleId]: initialStaffHash.subtabId } : {})
  });
  const [attendanceNoteDrafts, setAttendanceNoteDrafts] = useState({});
  const [selectedScopedMemberId, setSelectedScopedMemberId] = useState("");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSortOrder, setMemberSortOrder] = useState("newest");
  const [scopedAttendanceByEventId, setScopedAttendanceByEventId] = useState({});
  const [scopedAttendanceLoading, setScopedAttendanceLoading] = useState(false);
  const [messageDraft, setMessageDraft] = useState({
    audience: "chapter",
    subject: "",
    body: ""
  });
  const [savedMessageDrafts, setSavedMessageDrafts] = useState([]);
  const [editingDraftId, setEditingDraftId] = useState("");
  const [signoffNote, setSignoffNote] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [seededCalendarKey, setSeededCalendarKey] = useState("");
  const [calendarEventCursorByDay, setCalendarEventCursorByDay] = useState({});
  const scopedChapterIds = useMemo(() => accessChapterIds(selectedRange), [selectedRange]);
  const hasLimitedChapterScope = String(selectedRange?.access?.scopeMode || "").toLowerCase() === "limited" || scopedChapterIds.length > 0;
  const visibleChapters = useMemo(() => {
    if (!hasLimitedChapterScope || scopedChapterIds.length === 0) {
      return chapters;
    }
    const scopedIds = new Set(scopedChapterIds);
    return chapters.filter((chapter) => scopedIds.has(chapter.chapterId));
  }, [chapters, hasLimitedChapterScope, scopedChapterIds]);
  const scopedMembers = useMemo(() => {
    if (!hasLimitedChapterScope || scopedChapterIds.length === 0) {
      return members;
    }
    const scopedIds = new Set(scopedChapterIds);
    return members.filter((member) => memberChapterIds(member).some((chapterId) => scopedIds.has(chapterId)));
  }, [hasLimitedChapterScope, members, scopedChapterIds]);
  const scopedEvents = useMemo(() => {
    if (!hasLimitedChapterScope || scopedChapterIds.length === 0) {
      return events;
    }
    const scopedIds = new Set(scopedChapterIds);
    return events.filter((event) => eventChapterIds(event).some((chapterId) => scopedIds.has(chapterId)));
  }, [events, hasLimitedChapterScope, scopedChapterIds]);
  const selectedEvent = useMemo(
    () => scopedEvents.find((event) => event.eventId === selectedEventId) || null,
    [scopedEvents, selectedEventId]
  );
  const filteredEvents = useMemo(
    () => selectedEventFilters(scopedEvents, eventFilters || {}),
    [scopedEvents, eventFilters]
  );
  const selectedEventAttendanceByEventId = useMemo(() => {
    if (!selectedEventId) {
      return scopedAttendanceByEventId;
    }
    return {
      ...scopedAttendanceByEventId,
      [selectedEventId]: eventAttendance
    };
  }, [eventAttendance, scopedAttendanceByEventId, selectedEventId]);
  const attendanceByRegistrationId = useMemo(
    () => new Map(
      eventAttendance
        .filter((entry) => entry.registrationId)
        .map((entry) => [entry.registrationId, entry])
    ),
    [eventAttendance]
  );
  const attendanceByEmail = useMemo(
    () => new Map(
      eventAttendance
        .filter((entry) => entry.email)
        .map((entry) => [String(entry.email).trim().toLowerCase(), entry])
    ),
    [eventAttendance]
  );
  const selectedScopedMember = useMemo(
    () => scopedMembers.find((member) => (member.memberId || member.email) === selectedScopedMemberId) || null,
    [scopedMembers, selectedScopedMemberId]
  );
  const selectedScopedMemberJourney = useMemo(() => {
    if (!selectedScopedMember) {
      return null;
    }
    const memberEmail = String(selectedScopedMember.email || "").trim().toLowerCase();
    const memberId = String(selectedScopedMember.memberId || "").trim();
    const attendedClasses = scopedEvents
      .flatMap((event) => {
        const records = selectedEventAttendanceByEventId[event.eventId] || [];
        return records
          .filter((entry) => {
            const status = String(entry.status || "").toLowerCase();
            if (status && status !== "attended") {
              return false;
            }
            const attendanceMemberId = String(entry.memberId || entry.memberSnapshot?.memberId || "").trim();
            const attendanceEmail = String(entry.email || entry.memberSnapshot?.email || "").trim().toLowerCase();
            return (memberId && attendanceMemberId === memberId) || (memberEmail && attendanceEmail === memberEmail);
          })
          .map((attendance) => ({ event, attendance }));
      })
      .sort((left, right) => String(left.event.startsAt || "").localeCompare(String(right.event.startsAt || "")));
    const chapter = visibleChapters.find((item) => memberChapterIds(selectedScopedMember).includes(item.chapterId)) || null;
    return {
      member: selectedScopedMember,
      chapter,
      joinedAt: memberJoinDate(selectedScopedMember),
      attendedClasses,
      lastClass: attendedClasses[attendedClasses.length - 1]?.event || null
    };
  }, [scopedEvents, selectedEventAttendanceByEventId, selectedScopedMember, visibleChapters]);
  const chapterMemberVisibility = membershipSummary?.config?.settings?.chapterMemberVisibility || {};
  const chapterLeadMemberVisibility = {
    phone: Boolean(chapterMemberVisibility.phone),
    address: Boolean(chapterMemberVisibility.address),
    membershipStatus: Boolean(chapterMemberVisibility.membershipStatus),
    joinDate: Boolean(chapterMemberVisibility.joinDate),
    memberNumber: Boolean(chapterMemberVisibility.memberNumber)
  };
  const visibleScopedMembers = useMemo(() => {
    const needle = memberSearchQuery.trim().toLowerCase();
    return scopedMembers
      .filter((member) => {
        if (!needle) {
          return true;
        }
        return [
          memberName(member),
          member.email,
          chapterLeadMemberVisibility.phone ? member.phone : "",
          chapterLeadMemberVisibility.memberNumber ? member.memberNumber : "",
          memberChapterLabel(member, visibleChapters)
        ].join(" ").toLowerCase().includes(needle);
      })
      .sort((left, right) => {
        if (!chapterLeadMemberVisibility.joinDate) {
          return memberName(left).localeCompare(memberName(right));
        }
        const leftTime = memberJoinTimestamp(left);
        const rightTime = memberJoinTimestamp(right);
        return memberSortOrder === "oldest"
          ? leftTime - rightTime
          : rightTime - leftTime;
      });
  }, [chapterLeadMemberVisibility.joinDate, chapterLeadMemberVisibility.memberNumber, chapterLeadMemberVisibility.phone, memberSearchQuery, memberSortOrder, scopedMembers, visibleChapters]);

  useEffect(() => {
    const rangeId = selectedRange?.rangeId || "";
    const eventIds = scopedEvents.map((event) => event.eventId).filter(Boolean);
    if (!rangeId || eventIds.length === 0) {
      setScopedAttendanceByEventId({});
      setScopedAttendanceLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadScopedAttendance() {
      setScopedAttendanceLoading(true);
      const entries = await Promise.all(eventIds.map(async (eventId) => {
        try {
          const result = await listEventAttendance(rangeId, eventId);
          const records = Array.isArray(result) ? result : result?.attendance || result?.records || [];
          return [eventId, records];
        } catch {
          return [eventId, []];
        }
      }));
      if (!cancelled) {
        setScopedAttendanceByEventId(Object.fromEntries(entries));
        setScopedAttendanceLoading(false);
      }
    }

    loadScopedAttendance();
    return () => {
      cancelled = true;
    };
  }, [scopedEvents, selectedRange?.rangeId]);

  useEffect(() => {
    if (workspaceAppModule !== "members") {
      return;
    }
    const params = new URLSearchParams(location.search || "");
    const memberId = params.get("memberId") || "";
    const memberEmail = params.get("memberEmail") || "";
    if (!memberId && !memberEmail) {
      return;
    }
    const target = scopedMembers.find((member) => (
      (memberId && member.memberId === memberId) ||
      (memberEmail && String(member.email || "").toLowerCase() === memberEmail.toLowerCase())
    ));
    if (target) {
      setSelectedScopedMemberId(target.memberId || target.email || "");
    }
  }, [location.search, scopedMembers, workspaceAppModule]);

  function attendanceDraftKey(registration = {}) {
    return registration.registrationId || String(registration.email || "").trim().toLowerCase();
  }

  function attendanceForRegistration(registration = {}) {
    return attendanceByRegistrationId.get(registration.registrationId) ||
      attendanceByEmail.get(String(registration.email || "").trim().toLowerCase());
  }

  function recordAttendance(registration, status) {
    const key = attendanceDraftKey(registration);
    const hasDraft = Object.prototype.hasOwnProperty.call(attendanceNoteDrafts, key);
    const existingAttendance = attendanceForRegistration(registration);
    const notes = String(hasDraft ? attendanceNoteDrafts[key] : existingAttendance?.notes || "").trim();
    if (existingAttendance?.attendanceId && onUpdateEventAttendance) {
      onUpdateEventAttendance(existingAttendance, { status, notes });
    } else {
      onRecordEventAttendance(registration, status, notes);
    }
    setAttendanceNoteDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function submitClassSignoff(event) {
    event.preventDefault();
    if (!selectedEvent?.eventId || !onSignOffEvent) {
      return;
    }
    onSignOffEvent(selectedEvent.eventId, {
      note: signoffNote
    });
    setSignoffNote("");
  }

  function openMemberDetail(member) {
    setSelectedScopedMemberId(member.memberId || member.email || "");
  }

  function applyDraftIdea(event) {
    const template = draftTemplateForProgram(event, visibleChapters, selectedChapterTimezone);
    setSelectedEventId(event.eventId);
    setMessageDraft(template);
    setEditingDraftId("");
    switchSubtab("compose");
  }

  function submitDraftForReview(draftId) {
    setSavedMessageDrafts((current) => current.map((draft) => (
      draft.id === draftId
        ? { ...draft, status: "review_pending", submittedAt: new Date().toISOString() }
        : draft
    )));
  }

  const workspaceRole = effectiveWorkspaceRole(selectedRange, workspaceRoleOverride);
  const workspaceConfig = useMemo(() => roleWorkspaceConfig(selectedRange, workspaceRoleOverride), [selectedRange, workspaceRoleOverride]);
  const visibleModules = useMemo(
    () => workspaceConfig.moduleIds
      .filter((moduleId) => moduleAllowedByApplicationScope(moduleId, selectedRange))
      .map((moduleId) => ROLE_MODULES.find((module) => module.id === moduleId))
      .filter(Boolean),
    [selectedRange, workspaceConfig]
  );
  const displayModules = useMemo(
    () => visibleModules.map((module) => roleModuleConfig(module, workspaceRole)),
    [visibleModules, workspaceRole]
  );
  const visibleModuleIds = useMemo(() => new Set(visibleModules.map((module) => module.id)), [visibleModules]);

  useEffect(() => {
    const hash = (typeof window !== "undefined" && window.location.hash) || location.hash;
    const { moduleId, subtabId } = staffHashParts(hash);
    if (workspaceAppModule) {
      setActiveModule(workspaceAppModule);
      if (subtabId && moduleId === workspaceAppModule) {
        setActiveSubtabs((current) => ({ ...current, [workspaceAppModule]: subtabId }));
      }
      return;
    }
    if (!moduleId) {
      return;
    }
    if (!visibleModuleIds.has(moduleId)) {
      return;
    }
    setActiveModule(moduleId);
    if (subtabId) {
      setActiveSubtabs((current) => ({ ...current, [moduleId]: subtabId }));
    }
  }, [location.hash, visibleModuleIds, workspaceAppModule]);

  const activeModuleConfig = displayModules.find((module) => module.id === activeModule) || displayModules[0] || ROLE_MODULES[0];
  const activeModuleId = activeModuleConfig.id;
  const activeModuleSubtabs = workspaceConfig.subtabs?.[activeModuleId] || ROLE_SUBTABS[activeModuleId] || [];
  const activeSubtab = activeModuleSubtabs.some((label) => roleSubtabKey(label) === activeSubtabs[activeModuleId])
    ? activeSubtabs[activeModuleId]
    : roleSubtabKey(activeModuleSubtabs[0] || "");
  const routeControlledWorkspace = Boolean(workspaceAppBasePath);

  useEffect(() => {
    if (
      activeModuleId !== "programs" ||
      !["roster", "attendance", "prep", "signoff"].includes(activeSubtab) ||
      filteredEvents.length === 0
    ) {
      return;
    }
    if (!selectedEventId) {
      setSelectedEventId(filteredEvents[0].eventId);
      return;
    }
    const selectedStillVisible = filteredEvents.some((event) => event.eventId === selectedEventId);
    if (!selectedStillVisible) {
      setSelectedEventId("");
    }
  }, [activeModuleId, activeSubtab, filteredEvents, selectedEventId, setSelectedEventId]);
  const selectedChapterLabel = chapterScopeLabel(selectedRange, visibleChapters, eventFilters?.chapterId);
  const workspaceRangePath = workspaceAppBasePath || (selectedRange?.rangeId ? `/workspace/ranges/${encodeURIComponent(selectedRange.rangeId)}` : "");
  const selectedSettingsChapter = useMemo(() => {
    if (eventFilters?.chapterId) {
      return visibleChapters.find((chapter) => chapter.chapterId === eventFilters.chapterId) || visibleChapters[0] || null;
    }
    return visibleChapters[0] || null;
  }, [eventFilters?.chapterId, visibleChapters]);
  const selectedChapterTimezone = chapterTimezone(selectedRange, selectedSettingsChapter || visibleChapters[0] || {});
  const roleHomeSurface = ROLE_HOME_SURFACES[workspaceRole] || null;
  const workspaceRoleLabel = roleWorkspaceLabel(selectedRange, workspaceRoleOverride);
  const nextProgramForSummary = selectedEvent ||
    filteredEvents.find((event) => event.status === "published") ||
    filteredEvents[0] ||
    null;
  const commandFacts = roleHomeSurface?.commandFacts?.map((fact) => (
    fact.label === "Scope" ? { ...fact, value: selectedChapterLabel } : fact
  )) || [
    { label: "Programs", value: filteredEvents.length },
    { label: "Next class", value: nextProgramForSummary ? programDisplayTitle(nextProgramForSummary) : "No class selected" },
    { label: "Attendance", value: selectedEvent ? (eventAttendanceSummary?.total || eventAttendance.length) : 0 },
    { label: "Scope", value: selectedChapterLabel }
  ];
  const primaryWorkspaceAction = roleHomeSurface?.today || {
    action: nextProgramForSummary ? "Open next class" : "Open Programs & Events",
    moduleId: "programs",
    subtab: nextProgramForSummary ? "roster" : "upcoming"
  };
  const secondaryWorkspaceAction = visibleModuleIds.has("members")
    ? { action: "Open Members", moduleId: "members", subtab: workspaceRole === "chapter_leader" ? "members" : "roster" }
    : null;
  const commandSummaryFacts = commandFacts.filter((fact) => fact.label !== "Scope").slice(0, 3);
  const membershipTypes = membershipSummary?.membershipTypes || [];
  const membersNeedingReview = useMemo(
    () => scopedMembers.filter(memberNeedsReview),
    [scopedMembers]
  );
  const membersWithRequirements = useMemo(
    () => scopedMembers.filter((member) => (
      memberRequirementOpen(member, "waiver") ||
      memberRequirementOpen(member, "prequalification") ||
      memberRequirementOpen(member, "backgroundCheck")
    )),
    [scopedMembers]
  );
  const memberFollowUpRows = useMemo(() => {
    const rows = [];
    if (workspaceRole !== "chapter_leader") {
      membersNeedingReview.forEach((member) => {
        rows.push({
          key: `review-${member.memberId || member.email}`,
          type: "Review",
          member,
          title: memberName(member),
          detail: memberChapterLabel(member, visibleChapters),
          reason: "Needs review"
        });
      });
    }
    membersWithRequirements.forEach((member) => {
      if (rows.some((row) => row.member === member)) {
        return;
      }
      const openItems = [
        memberRequirementOpen(member, "waiver") ? "Waiver" : "",
        memberRequirementOpen(member, "prequalification") ? "Orientation" : "",
        memberRequirementOpen(member, "backgroundCheck") ? "Background check" : ""
      ].filter(Boolean);
      rows.push({
        key: `requirement-${member.memberId || member.email}`,
        type: "Requirement",
        member,
        title: memberName(member),
        detail: openItems.join(", ") || "Requirement follow-up",
        reason: memberEngagementLabel(member)
      });
    });
    return rows.slice(0, 12);
  }, [membersNeedingReview, membersWithRequirements, visibleChapters, workspaceRole]);
  const activeScopedMembers = useMemo(
    () => scopedMembers.filter((member) => ["active", "trial"].includes(String(member.expiration?.effectiveStatus || member.status || "").toLowerCase())),
    [scopedMembers]
  );
  const recentMemberJoins = useMemo(() => {
    const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return scopedMembers.filter((member) => {
      const joinedAt = Date.parse(member.createdAt || member.joinedAt || "");
      return Number.isFinite(joinedAt) && joinedAt >= cutoff;
    });
  }, [scopedMembers]);
  const openRosterCount = eventRegistrations.filter((registration) => ["confirmed", "waitlisted"].includes(registration.status)).length;
  const needsAttendanceCount = eventRegistrations.filter((registration) => {
    const attendance = attendanceForRegistration(registration);
    return ["confirmed", "waitlisted"].includes(registration.status) && !attendance;
  }).length;
  const manualAttendanceCount = eventAttendance.filter((entry) => entry.attendeeType === "manual" || entry.source === "manual").length;
  const selectedEventSignoff = selectedEvent?.completion?.status === "signed_off" ? selectedEvent.completion : null;
  const signoffDisabled = !selectedEvent || Boolean(selectedEventSignoff) || needsAttendanceCount > 0 || !eventAttendance.length || updatingEventId === selectedEvent?.eventId;
  const waitlistedRegistrations = useMemo(
    () => eventRegistrations.filter((registration) => registration.status === "waitlisted"),
    [eventRegistrations]
  );
  const paymentFollowUps = useMemo(
    () => eventRegistrations.filter((registration) => ["pending", "failed"].includes(registration.paymentStatus)),
    [eventRegistrations]
  );
  const upcomingPrograms = useMemo(
    () => filteredEvents.filter((event) => event.status === "published").slice(0, 4),
    [filteredEvents]
  );
  const classSchedule = useMemo(() => (
    (filteredEvents || [])
      .filter((event) => event.startsAt)
      .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)))
  ), [filteredEvents]);
  const calendarDays = useMemo(() => buildMonthDays(calendarMonth), [calendarMonth]);
  const eventsByDay = useMemo(() => classSchedule.reduce((map, event) => {
    const key = eventDateKey(event.startsAt);
    if (!key) {
      return map;
    }
    const current = map.get(key) || [];
    current.push(event);
    map.set(key, current);
    return map;
  }, new Map()), [classSchedule]);
  const selectedMonthKey = monthKey(calendarMonth);
  const now = new Date();
  const scopedTaskRows = useMemo(() => {
    const rows = [];
    if (needsAttendanceCount > 0) {
      rows.push({
        key: "attendance",
        type: "Attendance",
        title: `${needsAttendanceCount} attendance ${needsAttendanceCount === 1 ? "record" : "records"} still open`,
        detail: selectedEvent ? programDisplayTitle(selectedEvent) : "Select a class, then record attendance.",
        due: "Today",
        action: "Open Attendance",
        moduleId: "programs",
        subtab: "attendance"
      });
    }
    if (waitlistedRegistrations.length > 0) {
      rows.push({
        key: "waitlist",
        type: "Roster",
        title: `${waitlistedRegistrations.length} waitlist ${waitlistedRegistrations.length === 1 ? "decision" : "decisions"}`,
        detail: waitlistedRegistrations.slice(0, 2).map((registration) => registration.fullName || registration.email).join(", "),
        due: "Before class",
        action: "Open Roster",
        moduleId: "programs",
        subtab: "roster"
      });
    }
    if (workspaceRole !== "chapter_leader" && paymentFollowUps.length > 0) {
      rows.push({
        key: "payment",
        type: "Member follow-up",
        title: `${paymentFollowUps.length} payment ${paymentFollowUps.length === 1 ? "follow-up" : "follow-ups"}`,
        detail: paymentFollowUps.slice(0, 2).map((registration) => registration.fullName || registration.email).join(", "),
        due: "Before check-in",
        action: "Open Roster",
        moduleId: "programs",
        subtab: "roster"
      });
    }
    upcomingPrograms.slice(0, 2).forEach((event) => {
      rows.push({
        key: `class-${event.eventId}`,
        type: "Class",
        title: programDisplayTitle(event),
        detail: programBrief(event, visibleChapters, selectedChapterTimezone),
        due: "Upcoming",
        action: "Open Class",
        moduleId: "programs",
        subtab: "roster"
      });
    });
    return rows;
  }, [needsAttendanceCount, paymentFollowUps, selectedChapterTimezone, selectedEvent, upcomingPrograms, visibleChapters, waitlistedRegistrations, workspaceRole]);
  const completedTaskRows = useMemo(
    () => eventAttendance.slice(0, 4).map((entry, index) => ({
      key: entry.attendanceId || `${entry.email}-${entry.checkedInAt}-${index}`,
      type: "Attendance recorded",
      title: entry.fullName || entry.email || "Participant",
      detail: statusLabel(entry.status),
      due: formatDateTime(entry.checkedInAt || entry.createdAt, selectedChapterTimezone)
    })),
    [eventAttendance, selectedChapterTimezone]
  );
  const attendanceAttendedCount = selectedEvent ? (eventAttendanceSummary?.attended || eventAttendance.filter((entry) => entry.status === "attended").length) : 0;
  const attendanceNoShowCount = selectedEvent ? (eventAttendanceSummary?.noShow || eventAttendance.filter((entry) => entry.status === "no_show").length) : 0;
  const attendanceExcusedCount = selectedEvent ? (eventAttendanceSummary?.excused || eventAttendance.filter((entry) => entry.status === "excused").length) : 0;
  const attendanceTotalCount = selectedEvent ? (eventAttendanceSummary?.total || eventAttendance.length) : 0;
  const confirmedRosterCount = eventRegistrationsSummary?.confirmed || eventRegistrations.filter((registration) => registration.status === "confirmed").length;
  const waitlistCount = eventRegistrationsSummary?.waitlisted || waitlistedRegistrations.length;
  const selectedRosterSummaryText = selectedClassAttendanceSummary({
    attended: attendanceAttendedCount,
    noShow: attendanceNoShowCount,
    open: needsAttendanceCount,
    roster: eventRegistrations.length,
    waitlisted: waitlistCount
  });
  const readinessPressure = membersWithRequirements.length + needsAttendanceCount + waitlistedRegistrations.length + (workspaceRole === "chapter_leader" ? 0 : membersNeedingReview.length + paymentFollowUps.length);
	  const homeAttentionRows = useMemo(() => {
	    const updateRows = notifications
	      .filter((notification) => String(notification.audience || "").toLowerCase() === "chapter_leader")
	      .filter((notification) => {
	        const chapterId = notification.related?.chapterId || notification.metadata?.chapterId || "";
	        return !hasLimitedChapterScope || scopedChapterIds.length === 0 || !chapterId || scopedChapterIds.includes(chapterId);
	      })
	      .slice(0, 2)
	      .map((notification) => {
	        const type = notification.type === "chapter.member_added" ? "New member" : "Update";
	        return {
	          key: `notice-${notification.notificationId}`,
	          type,
	          title: notification.subject || "New update",
	          detail: notification.body || notification.related?.chapterName || selectedChapterLabel,
	          due: formatDateTime(notification.createdAt || notification.updatedAt, selectedChapterTimezone),
              action: notification.related?.eventId ? "Open Class" : "Open Members",
              moduleId: notification.related?.eventId ? "programs" : "members",
              subtab: notification.related?.eventId ? "attendance" : "directory"
	        };
	      });
	    const rows = [
	      ...updateRows,
      ...(workspaceRole === "chapter_leader" ? [] : membersWithRequirements.slice(0, 2).map((member) => ({
	        key: `requirement-${member.memberId || member.email}`,
        type: "Requirement",
        title: memberName(member),
        detail: memberEngagementLabel(member),
        due: "Member follow-up",
        action: "Open Follow-up",
        moduleId: "members",
        subtab: "follow-up"
      }))),
      ...scopedTaskRows
    ];

    return rows
	      .filter((row, index, list) => list.findIndex((item) => item.key === row.key) === index)
	      .slice(0, 6);
	  }, [hasLimitedChapterScope, membersWithRequirements, notifications, scopedChapterIds, scopedTaskRows, selectedChapterLabel, selectedChapterTimezone, workspaceRole]);
  const homeActivityRows = useMemo(() => [
    ...completedTaskRows.slice(0, 3).map((row) => ({
      ...row,
      action: "View Attendance",
      moduleId: "programs",
      subtab: "attendance"
    })),
    ...recentMemberJoins.slice(0, 3).map((member) => ({
      key: `join-${member.memberId || member.email}`,
      type: "Recent join",
      title: memberName(member),
      detail: memberChapterLabel(member, visibleChapters),
        due: formatDateTime(member.createdAt || member.joinedAt, selectedChapterTimezone),
      action: "Open Members",
      moduleId: "members",
      subtab: workspaceRole === "chapter_leader" ? "members" : "directory"
    })),
    ...upcomingPrograms.slice(0, 2).map((event) => ({
      key: `upcoming-${event.eventId}`,
      type: "Class scheduled",
      title: programDisplayTitle(event),
      detail: programBrief(event, visibleChapters, selectedChapterTimezone),
      due: statusLabel(event.status),
      action: "Open Class",
      moduleId: "programs",
      subtab: "upcoming"
    }))
  ].slice(0, 6), [completedTaskRows, recentMemberJoins, selectedChapterTimezone, upcomingPrograms, visibleChapters, workspaceRole]);
  const homeHandoffRows = useMemo(() => [
    {
      key: "next-program",
      label: "Next class",
      value: selectedEvent ? programDisplayTitle(selectedEvent) : upcomingPrograms[0] ? programDisplayTitle(upcomingPrograms[0]) : "No class selected",
      detail: selectedEvent ? programBrief(selectedEvent, visibleChapters, selectedChapterTimezone) : upcomingPrograms[0] ? programBrief(upcomingPrograms[0], visibleChapters, selectedChapterTimezone) : "Open Programs & Events when the next class is assigned."
    },
    {
      key: "open-work",
      label: "Open work",
      value: readinessPressure > 0 ? `${readinessPressure} signals` : "Quiet",
      detail: readinessPressure > 0 ? "Requirements, attendance, or roster follow-up need attention." : "No urgent scoped work is visible right now."
    },
    {
      key: "local-contact",
      label: "Local contact",
      value: selectedSettingsChapter?.contactEmail || chapterLeaderLabel(selectedSettingsChapter || {}),
      detail: "Access grants and scope changes stay with organization admins."
    }
  ], [readinessPressure, selectedChapterTimezone, selectedEvent, selectedSettingsChapter, upcomingPrograms, visibleChapters]);
  const selectedProgramPrepRows = useMemo(() => {
    if (!selectedEvent) {
      return [];
    }
    return [
      {
        key: "time",
        type: "When",
        title: formatDateTime(selectedEvent.startsAt, selectedChapterTimezone),
        detail: selectedEvent.endsAt ? `Ends ${formatDateTime(selectedEvent.endsAt, selectedChapterTimezone)}` : "End time not listed",
        signal: selectedEvent.startsAt ? "Ready" : "Needs time"
      },
      {
        key: "location",
        type: "Where",
        title: selectedEvent.location || "Location not listed",
        detail: formatChapter(selectedEvent, visibleChapters),
        signal: selectedEvent.location ? "Ready" : "Needs location"
      },
      {
        key: "instructors",
        type: "Coverage",
        title: programInstructorLabel(selectedEvent),
        detail: "Program setup stays in Programs & Events.",
        signal: Array.isArray(selectedEvent.instructors) && selectedEvent.instructors.length > 0 ? "Ready" : "Needs coverage"
      },
      {
        key: "capacity",
        type: "Capacity",
        title: eventRegistrations.length > 0 ? `${eventRegistrations.length} people listed` : programCapacityLabel(selectedEvent, confirmedRosterCount),
        detail: selectedRosterSummaryText,
        signal: waitlistCount > 0 ? "Watch waitlist" : "Ready"
      },
      {
        key: "registration",
        type: "Registration",
        title: programRegistrationWindowLabel(selectedEvent, selectedChapterTimezone),
        detail: `${eventRegistrations.length} roster ${eventRegistrations.length === 1 ? "record" : "records"} visible`,
        signal: eventRegistrations.length > 0 ? "Roster started" : "No roster yet"
      },
      {
        key: "follow-up",
        type: "Follow-up",
        title: `${needsAttendanceCount} attendance left`,
        detail: `${paymentFollowUps.length} payment ${paymentFollowUps.length === 1 ? "follow-up" : "follow-ups"}`,
        signal: needsAttendanceCount + paymentFollowUps.length > 0 ? "Needs lead action" : "Ready"
      }
    ];
  }, [confirmedRosterCount, eventRegistrations.length, needsAttendanceCount, paymentFollowUps.length, selectedChapterTimezone, selectedEvent, selectedRosterSummaryText, visibleChapters, waitlistCount]);
  const chapterReadinessRows = useMemo(() => ([
    {
      label: "Profile description",
      value: selectedSettingsChapter?.description ? "Ready" : "Needs admin update",
      detail: selectedSettingsChapter?.description || "No local description is visible yet."
    },
    {
      label: "Meeting area",
      value: selectedSettingsChapter?.meetingArea || chapterLocation(selectedSettingsChapter || {}),
      detail: selectedSettingsChapter?.meetingArea ? "Local meeting area is listed." : "Meeting area is not set."
    },
    {
      label: "Public contact",
      value: selectedSettingsChapter?.contactEmail || "No public contact",
      detail: selectedSettingsChapter?.contactEmail ? "Members have a local contact path." : "Chapters should add a public contact."
    },
    {
      label: "Lead coverage",
      value: chapterLeaderLabel(selectedSettingsChapter || {}),
      detail: chapterLeaders(selectedSettingsChapter || {}).length > 0 ? "Local lead coverage is listed." : "Access grants still stay in Access."
    }
  ]), [selectedSettingsChapter]);

  const calendarSeedKey = useMemo(() => (
    `${selectedRange?.rangeId || "range"}:${classSchedule.map((event) => event.eventId || event.startsAt || event.title).join("|")}`
  ), [classSchedule, selectedRange?.rangeId]);

  useEffect(() => {
    if (classSchedule.length === 0 || seededCalendarKey === calendarSeedKey) {
      return;
    }
    const currentMonthHasClasses = classSchedule.some((event) => {
      const startsAt = new Date(event.startsAt);
      return !Number.isNaN(startsAt.getTime()) && monthKey(startsAt) === monthKey(calendarMonth);
    });
    if (!currentMonthHasClasses) {
      const nowTime = Date.now();
      const nearestUpcoming = classSchedule.find((event) => {
        const eventTime = Date.parse(event.endsAt || event.startsAt);
        return Number.isFinite(eventTime) && eventTime >= nowTime;
      });
      const nearestClass = nearestUpcoming || classSchedule[classSchedule.length - 1];
      const startsAt = new Date(nearestClass.startsAt);
      if (!Number.isNaN(startsAt.getTime())) {
        setCalendarMonth(new Date(startsAt.getFullYear(), startsAt.getMonth(), 1));
      }
    }
    setSeededCalendarKey(calendarSeedKey);
  }, [calendarMonth, calendarSeedKey, classSchedule, seededCalendarKey]);

  useEffect(() => {
    if (!visibleModuleIds.has(activeModule)) {
      setActiveModule(visibleModules[0]?.id || "home");
    }
  }, [activeModule, visibleModuleIds, visibleModules]);

  function switchModule(moduleId) {
    if (!visibleModuleIds.has(moduleId)) {
      return;
    }
    if (workspaceAppBasePath) {
      navigate(`${workspaceAppBasePath}/apps/${workspaceAppSlugForModule(moduleId)}`);
      return;
    }
    setActiveModule(moduleId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#staff-${moduleId}`);
    }
  }

  function switchSubtab(tabKey) {
    setActiveSubtabs((current) => ({ ...current, [activeModuleId]: tabKey }));
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#staff-${activeModuleId}-${tabKey}`);
    }
  }

  function openWorkspaceTask(row) {
    if (!row?.moduleId || !visibleModuleIds.has(row.moduleId)) {
      return;
    }
    if (workspaceAppBasePath) {
      setActiveSubtabs((current) => row.subtab ? { ...current, [row.moduleId]: row.subtab } : current);
      navigate(`${workspaceAppBasePath}/apps/${workspaceAppSlugForModule(row.moduleId)}${row.subtab ? `#staff-${row.moduleId}-${row.subtab}` : ""}`);
      return;
    }
    setActiveModule(row.moduleId);
    if (row.subtab) {
      setActiveSubtabs((current) => ({ ...current, [row.moduleId]: row.subtab }));
    }
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#staff-${row.moduleId}${row.subtab ? `-${row.subtab}` : ""}`);
    }
  }

  function openProgramWorkspace(event, subtab = "roster") {
    if (!event?.eventId) {
      return;
    }
    setSelectedEventId(event.eventId);
    if (workspaceAppBasePath) {
      setActiveSubtabs((current) => ({ ...current, programs: subtab }));
      navigate(`${workspaceAppBasePath}/apps/classes#staff-programs-${subtab}`);
      return;
    }
    setActiveModule("programs");
    setActiveSubtabs((current) => ({ ...current, programs: subtab }));
  }

  function renderProgramCard(event, mode = "upcoming") {
    const isSelected = selectedEventId === event.eventId;
    const isChapterLeadWorkspace = workspaceRole === "chapter_leader";
    const targetSubtab = mode === "attendance" ? "attendance" : mode === "signoff" ? "signoff" : "roster";
    if (isChapterLeadWorkspace) {
      return (
        <div
          className={`event-program-card chapter-lead-class-card ${isSelected ? "is-selected" : ""}`}
          key={event.eventId}
          role="button"
          tabIndex={0}
          onClick={() => openProgramWorkspace(event, targetSubtab)}
          onKeyDown={(keyboardEvent) => {
            if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
              keyboardEvent.preventDefault();
              openProgramWorkspace(event, targetSubtab);
            }
          }}
        >
          <div className="event-date-block">
            <span>{formatDateTime(event.startsAt, selectedChapterTimezone).split(",")[0] || "Date TBD"}</span>
            <strong>{eventTimeRange(event) || "Time TBD"}</strong>
          </div>
          <div className="event-program-main">
            <div className="event-program-title">
              <strong>{programDisplayTitle(event)}</strong>
              <em>{event.completion?.status === "signed_off" ? "Signed off" : statusLabel(event.status || "draft")}</em>
            </div>
            <div className="event-program-facts">
              <span>Location: {event.location || "Not set"}</span>
              <span>Instructor: {programInstructorLabel(event)}</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <article
        className={`staff-program-row ${isSelected ? "is-selected" : ""}`}
        key={event.eventId}
      >
        <span className={`staff-status-dot ${statusClass(event.status)}`} />
        <span>
          <strong>{programDisplayTitle(event)}</strong>
          <small>{formatDateTime(event.startsAt, selectedChapterTimezone)} / {event.location || "Location not set"}</small>
        </span>
        <em>{formatChapter(event, visibleChapters)}</em>
        <small>{eventRosterCount(event)}</small>
        <div className="staff-program-row-actions">
          {isChapterLeadWorkspace ? (
            <button type="button" onClick={() => openProgramWorkspace(event, targetSubtab)}>
              Open
            </button>
          ) : (
            <>
              <button type="button" onClick={() => openProgramWorkspace(event, "roster")}>
                Roster
              </button>
              <button type="button" onClick={() => openProgramWorkspace(event, "attendance")}>
                Attendance
              </button>
              <button type="button" onClick={() => openProgramWorkspace(event, mode === "upcoming" ? "prep" : "prep")}>
                Prep
              </button>
            </>
          )}
        </div>
      </article>
    );
  }

  return (
    <div className="staff-portal-page role-workspace-page">
      {loading && <div className="status-strip">Refreshing chapter lead portal...</div>}

      {!routeControlledWorkspace && (
      <section className="staff-command-strip">
        <div className="staff-command-title">
          <span className="section-kicker">{workspaceRoleLabel}</span>
          <h2>{selectedRange?.displayName || selectedRange?.name || "Active workspace"}</h2>
          <div className="staff-command-meta" aria-label="Workspace summary">
            <span>{selectedChapterLabel}</span>
            {commandSummaryFacts.map((fact) => (
              <span key={fact.label}>{fact.label}: {fact.value}</span>
            ))}
          </div>
        </div>
        <div className="staff-command-actions">
          <button type="button" onClick={() => openWorkspaceTask(primaryWorkspaceAction)}>
            {primaryWorkspaceAction.action}
          </button>
          {secondaryWorkspaceAction && (
            <button type="button" className="secondary-button" onClick={() => openWorkspaceTask(secondaryWorkspaceAction)}>
              {secondaryWorkspaceAction.action}
            </button>
          )}
        </div>
      </section>
      )}

      {!routeControlledWorkspace && (
        <nav className="role-workspace-module-nav" aria-label="Role workspace modules">
          {displayModules.map((module) => (
            <button
              type="button"
              aria-label={`Open ${module.label}`}
              className={activeModuleId === module.id ? "is-active" : ""}
              key={module.id}
              onClick={() => switchModule(module.id)}
            >
              <span>{module.label}</span>
              <small>{module.meta}</small>
            </button>
          ))}
        </nav>
      )}

      <section className="role-workspace-module">
        {!routeControlledWorkspace && (
        <div className="role-workspace-module-header">
          <div>
            <span className="section-kicker">{activeModuleConfig.meta}</span>
            <h3>{activeModuleConfig.label}</h3>
          </div>
          <div className="role-workspace-scope-pill">{selectedChapterLabel}</div>
        </div>
        )}

        {activeModuleSubtabs.length > 1 && (
          <nav className="role-workspace-subtab-nav" aria-label={`${activeModuleConfig.label} tabs`}>
            {activeModuleSubtabs.map((label) => {
              const key = roleSubtabKey(label);
              const displayLabel = displayRoleSubtabLabel(activeModuleId, label);
              return (
                <button
                  type="button"
                  aria-label={`Open ${activeModuleConfig.label} ${displayLabel} tab`}
                  className={activeSubtab === key ? "is-active" : ""}
                  key={key}
                  onClick={() => switchSubtab(key)}
                >
                  {displayLabel}
                </button>
              );
            })}
          </nav>
        )}

        {activeModuleId === "home" && (
          <div className="role-workspace-dashboard-grid">
            {activeSubtab === "today" && (
              <section className="panel organization-calendar-panel chapter-lead-calendar-panel">
                <div className="organization-calendar-header">
                  <div>
                    <span className="section-kicker">Class calendar</span>
                    <h3>{monthLabel(calendarMonth)}</h3>
                  </div>
                  <div className="organization-calendar-controls">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setCalendarMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="organization-month-grid" aria-label={`${monthLabel(calendarMonth)} class schedule`}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div className="organization-weekday" key={day}>{day}</div>
                  ))}
                  {calendarDays.map((day) => {
                    const key = eventDateKey(day);
                    const dayEvents = eventsByDay.get(key) || [];
                    const activeEventIndex = dayEvents.length > 0
                      ? ((calendarEventCursorByDay[key] || 0) % dayEvents.length + dayEvents.length) % dayEvents.length
                      : 0;
                    const visibleEvent = dayEvents[activeEventIndex];
                    const outsideMonth = monthKey(day) !== selectedMonthKey;
                    const today = eventDateKey(day) === eventDateKey(now);
                    return (
                      <div
                        className={`organization-calendar-day${outsideMonth ? " is-outside-month" : ""}${today ? " is-today" : ""}${dayEvents.length ? " has-events" : ""}`}
                        key={key}
                      >
                        <div className="organization-calendar-day-label">
                          <strong>{day.getDate()}</strong>
                          <span className="organization-calendar-day-badges">
                            {today && <span>Today</span>}
                          </span>
                        </div>
                        <div className="organization-calendar-events">
                          {visibleEvent && (
                            <button
                              type="button"
                              className={`organization-calendar-event is-${scheduleStatus(visibleEvent, now).toLowerCase().replace(/\s+/g, "-")}`}
                              key={visibleEvent.eventId || `${visibleEvent.title}-${visibleEvent.startsAt}`}
                              onClick={() => openProgramWorkspace(visibleEvent, "roster")}
                            >
                              <span>{eventTimeRange(visibleEvent)}</span>
                              <strong>{programDisplayTitle(visibleEvent)}</strong>
                              <small>{formatChapter(visibleEvent, visibleChapters)}</small>
                            </button>
                          )}
                          {dayEvents.length > 1 && (
                            <div className="organization-calendar-event-pager" aria-label={`${dayEvents.length} classes on ${key}`}>
                              <button
                                type="button"
                                aria-label={`Previous class on ${key}`}
                                onClick={() => setCalendarEventCursorByDay((current) => ({
                                  ...current,
                                  [key]: (((current[key] || 0) - 1 + dayEvents.length) % dayEvents.length)
                                }))}
                              >
                                ‹
                              </button>
                              <span>{activeEventIndex + 1}/{dayEvents.length}</span>
                              <button
                                type="button"
                                aria-label={`Next class on ${key}`}
                                onClick={() => setCalendarEventCursorByDay((current) => ({
                                  ...current,
                                  [key]: (((current[key] || 0) + 1) % dayEvents.length)
                                }))}
                              >
                                ›
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {classSchedule.length === 0 && (
                  <div className="dashboard-empty-state">
                    <strong>No classes scheduled</strong>
                    <span>Classes assigned to this chapter appear here.</span>
                  </div>
                )}
              </section>
            )}
            {activeSubtab === "attention" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Attention</span>
                    <h3>Needs attention</h3>
                  </div>
                  <div className="role-workspace-list">
                    {(roleHomeSurface?.attention || homeAttentionRows).length === 0 ? (
                      <div className="compact-status-row">No member or class follow-up is visible in this scope.</div>
                    ) : (roleHomeSurface?.attention || homeAttentionRows).map((row) => (
                      <article className="role-workspace-list-row" key={row.key}>
                        <span className={`staff-pill ${row.type === "Attendance" || row.type === "Member follow-up" || row.type === "Requirement" || row.type === "Review" ? "is-warning" : ""}`}>{row.type}</span>
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{row.due}</em>
                        <button type="button" onClick={() => openWorkspaceTask(row)}>
                          {row.action}
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Focus</span>
                  <div className="role-workspace-preview-list">
                    {(roleHomeSurface?.today.facts || [
                      { label: "Requirements", value: membersWithRequirements.length },
                      { label: "Attendance left", value: needsAttendanceCount },
                      { label: "Waitlist", value: waitlistCount }
                    ]).map((fact) => (
                      <div key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>
                    ))}
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "activity" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Activity</span>
                    <h3>Recent movement in this scope</h3>
                    <p>Activity stays focused on local joins, attendance updates, and upcoming class movement.</p>
                  </div>
                  <div className="role-workspace-list">
                    {homeActivityRows.length === 0 ? (
                      <div className="compact-status-row">No recent scoped activity is visible yet.</div>
                    ) : homeActivityRows.map((row) => (
                      <article className="role-workspace-list-row" key={row.key}>
                        <span className="staff-pill">{row.type}</span>
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{row.due}</em>
                        <button type="button" onClick={() => openWorkspaceTask(row)}>
                          {row.action}
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Local picture</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Recent joins</span><strong>{recentMemberJoins.length}</strong></div>
                    <div><span>Attendance records</span><strong>{eventAttendance.length}</strong></div>
                    <div><span>Upcoming classes</span><strong>{upcomingPrograms.length}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "handoff" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Handoff</span>
                    <h3>{roleHomeSurface ? "What the next operator should know" : "What the next lead should know"}</h3>
                    <p>{roleHomeSurface ? "Handoff keeps the role's next action, boundaries, and work history close." : "Handoff summarizes the next program, open work, and local contact coverage."}</p>
                  </div>
                  <div className="role-workspace-list">
                    {(roleHomeSurface?.handoff || homeHandoffRows).map((row) => (
                      <article className="role-workspace-list-row" key={row.key}>
                        <span className="staff-pill">{row.label}</span>
                        <div>
                          <strong>{row.value}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{selectedChapterLabel}</em>
                      </article>
                    ))}
                    <article className="role-workspace-list-row is-actionable">
                      <span className="staff-pill is-warning">Participant notes</span>
                      <div>
                        <strong>Leave a clear class handoff</strong>
                        <small>Select a class, update attendance, and add only the context the owner needs after sign-off.</small>
                      </div>
                      <em>{selectedEvent ? programDisplayTitle(selectedEvent) : "Choose a program"}</em>
                      <button type="button" onClick={() => openWorkspaceTask({ moduleId: "programs", subtab: "roster" })}>
                        Open roster handoff
                      </button>
                    </article>
                    {visibleModuleIds.has("messages") && (
                      <article className="role-workspace-list-row is-actionable">
                        <span className="staff-pill">Message draft</span>
                        <div>
                          <strong>Prepare the local follow-up before delivery review</strong>
                          <small>Use a scoped draft for the chapter or selected roster, then review sending from Communications and Notifications.</small>
                        </div>
                        <em>{savedMessageDrafts.length} saved</em>
                        <button type="button" onClick={() => openWorkspaceTask({ moduleId: "messages", subtab: savedMessageDrafts.length ? "drafts" : "compose" })}>
                          Open message draft
                        </button>
                      </article>
                    )}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Boundary</span>
                  <p>{roleHomeSurface ? "Operator context belongs here. Role scope, sign-in access, policy, billing, and organization setup remain in admin tools." : "Lead-to-lead context belongs here. Role scope, sign-in access, billing decisions, and organization setup remain in admin tools."}</p>
                </aside>
              </>
            )}
          </div>
        )}

        {activeModuleId === "members" && (
          <div className="role-workspace-dashboard-grid chapter-lead-members-grid">
            {(activeSubtab === "members" || activeSubtab === "directory" || activeSubtab === "roster") && (
              <>
                {selectedScopedMember ? (
                  <section className="role-workspace-list-panel chapter-lead-member-focus">
                    <div className="chapter-lead-detail-header">
                      <div>
                        <span className="section-kicker">Member</span>
                        <h3>{memberName(selectedScopedMember)}</h3>
                      </div>
                      <button type="button" className="secondary-button" onClick={() => setSelectedScopedMemberId("")}>
                        Back to members
                      </button>
                    </div>
                    <div className="chapter-lead-member-profile-grid">
                      {chapterLeadMemberProfileRows(
                        selectedScopedMember,
                        selectedScopedMemberJourney,
                        membershipTypes,
                        visibleChapters,
                        selectedChapterTimezone,
                        chapterLeadMemberVisibility
                      ).map((row) => (
                        <div className={row.wide ? "is-wide" : ""} key={row.label}>
                          <span>{row.label}</span>
                          <strong>{row.value}</strong>
                        </div>
                      ))}
                    </div>
                    <div className="chapter-lead-member-journey">
                      <div className="chapter-lead-member-journey-title">
                        <span className="section-kicker">Journey</span>
                        {scopedAttendanceLoading && <small>Refreshing class history...</small>}
                      </div>
                      <div className="org-insights-member-timeline chapter-lead-member-timeline">
                        {[...(selectedScopedMemberJourney?.attendedClasses || [])].reverse().map(({ event, attendance }) => (
                          <article className="is-class" key={`${event.eventId}-${attendance.attendanceId || attendance.email || selectedScopedMember.email}`}>
                            <time>{formatDateOnly(event.startsAt, selectedChapterTimezone)}</time>
                            <div>
                              <strong>{programDisplayTitle(event)}</strong>
                              <span>{programBrief(event, visibleChapters, selectedChapterTimezone)}</span>
                            </div>
                            {workspaceRangePath ? (
                              <a href={`${workspaceRangePath}/apps/events?eventId=${encodeURIComponent(event.eventId)}#events-roster`}>
                                Open class
                              </a>
                            ) : (
                              <span className="org-insights-journey-kind">Attendance</span>
                            )}
                          </article>
                        ))}
                        {(selectedScopedMemberJourney?.attendedClasses.length || 0) === 0 && (
                          <div className="org-insights-empty">No class attendance recorded yet.</div>
                        )}
                        {chapterLeadMemberVisibility.joinDate && selectedScopedMemberJourney?.joinedAt && memberJoinTimestamp(selectedScopedMember) > 0 && (
                          <article className="is-joined">
                            <time>{formatDateOnly(selectedScopedMemberJourney.joinedAt, selectedChapterTimezone)}</time>
                            <div>
                              <strong>Joined {selectedScopedMemberJourney.chapter?.name || memberChapterLabel(selectedScopedMember, visibleChapters)}</strong>
                              {visibleText(selectedScopedMember.email) && <span>{selectedScopedMember.email}</span>}
                            </div>
                          </article>
                        )}
                      </div>
                    </div>
                  </section>
                ) : (
                  <>
                    <section className="role-workspace-list-panel chapter-lead-directory">
                      <div>
                        <span className="section-kicker">Members</span>
                        <h3>{selectedChapterLabel} members</h3>
                      </div>
                      <div className="workspace-filters chapter-lead-member-filters">
                        <label>
                          Search
                          <input
                            value={memberSearchQuery}
                            placeholder={chapterLeadMemberVisibility.phone ? "Name, email, or phone" : "Name or email"}
                            onChange={(event) => setMemberSearchQuery(event.target.value)}
                          />
                        </label>
                        {chapterLeadMemberVisibility.joinDate && (
                          <label>
                            Joined
                            <select
                              value={memberSortOrder}
                              onChange={(event) => setMemberSortOrder(event.target.value)}
                            >
                              <option value="newest">Newest first</option>
                              <option value="oldest">Oldest first</option>
                            </select>
                          </label>
                        )}
                      </div>
                      {membersLoading && <div className="compact-status-row">Refreshing members...</div>}
                      {!membersLoading && scopedMembers.length === 0 && (
                        <div className="compact-status-row">No members are visible yet.</div>
                      )}
                      {!membersLoading && scopedMembers.length > 0 && visibleScopedMembers.length === 0 && (
                        <div className="compact-status-row">No members match that search.</div>
                      )}
                      <div className="role-workspace-list">
                        {visibleScopedMembers.map((member) => (
                          <article className={`role-workspace-list-row chapter-lead-member-row ${chapterLeadMemberVisibility.membershipStatus ? "" : "is-status-hidden"}`} key={member.memberId || `${member.email}-${member.fullName}`}>
                            {chapterLeadMemberVisibility.membershipStatus ? (
                              <span className={`staff-pill ${statusClass(member.expiration?.effectiveStatus || member.status)}`}>{memberAccessLabel(member)}</span>
                            ) : null}
                            <div>
                              <strong>{memberName(member)}</strong>
                              <small>
                                {member.email || "Email not set"}
                                {chapterLeadMemberVisibility.phone && member.phone ? ` / ${member.phone}` : ""}
                              </small>
                            </div>
                            {chapterLeadMemberVisibility.joinDate && <em>{memberEngagementLabel(member)}</em>}
                            <button type="button" onClick={() => openMemberDetail(member)}>View</button>
                          </article>
                        ))}
                      </div>
                    </section>
                  </>
                )}
              </>
            )}
            {workspaceRole !== "chapter_leader" && (activeSubtab === "follow-up" || activeSubtab === "needs-review" || activeSubtab === "requirements") && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Follow-up</span>
                    <h3>Members needing attention</h3>
                  </div>
                  <div className="role-workspace-list">
                    {memberFollowUpRows.length === 0 ? (
                      <div className="compact-status-row">No member follow-up is open.</div>
                    ) : memberFollowUpRows.map((row) => (
                      <article className="role-workspace-list-row chapter-lead-member-row" key={row.key}>
                        <span className="staff-pill is-warning">{row.type}</span>
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{row.reason}</em>
                        <button type="button" onClick={() => openMemberDetail(row.member)}>View</button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Today</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Open items</span><strong>{memberFollowUpRows.length}</strong></div>
                    <div><span>Requirements</span><strong>{membersWithRequirements.length}</strong></div>
                  </div>
                </aside>
              </>
            )}
          </div>
        )}

        {activeModuleId === "programs" && (
          <>
            {activeSubtab === "upcoming" && (
              <section className="member-list events-schedule-list chapter-lead-events-list">
                  <div className="panel-header">
                    <div>
                      <h3>Classes</h3>
                    </div>
                  </div>
                  <div className="chapter-lead-schedule-list">
                    {filteredEvents.length === 0 ? (
                      <div className="empty-state compact-empty">
                        <strong>No classes match this view.</strong>
                        <span>{filterAwareProgramEmptyCopy(eventFilters, selectedChapterLabel)}</span>
                      </div>
                    ) : filteredEvents.map((event) => renderProgramCard(event, "upcoming"))}
                  </div>
              </section>
            )}

            {["roster", "attendance", "signoff", "prep"].includes(activeSubtab) && (
              <section className="staff-workbench role-program-workbench chapter-lead-class-workbench">
                <aside className="staff-program-panel">
                  <div className="panel-header">
                    <div>
                      <h3>Classes</h3>
                    </div>
                  </div>

                  <div className="staff-program-list">
                    {filteredEvents.length === 0 ? (
                      <div className="empty-state compact-empty">
                        <strong>No classes match this view.</strong>
                        <span>{filterAwareProgramEmptyCopy(eventFilters, selectedChapterLabel)}</span>
                      </div>
                    ) : filteredEvents.map((event) => renderProgramCard(event, activeSubtab))}
                  </div>
                </aside>

                <section className="staff-roster-panel chapter-lead-class-panel">
                  {!selectedEvent ? (
                    <div className="staff-roster-empty">
                      <span className="section-kicker">Class</span>
                      <h3>Select a class</h3>
                    </div>
                  ) : (
                    <>
                      <div className="staff-selected-program">
                        <div>
                          <span className="section-kicker">{formatChapter(selectedEvent, visibleChapters)}</span>
                          <h3>{programDisplayTitle(selectedEvent)}</h3>
                          <p>{formatDateTime(selectedEvent.startsAt, selectedChapterTimezone)} / {selectedEvent.location || "Location not set"}</p>
                        </div>
                        <button type="button" className="secondary-button" onClick={() => setSelectedEventId("")}>
                          Change
                        </button>
                      </div>

                      {activeSubtab === "prep" && (
                        <div className="role-workspace-dashboard-grid">
                          <section className="role-workspace-list-panel">
                            <div>
                              <span className="section-kicker">Prep</span>
                              <h3>Before class</h3>
                            </div>
                            <div className="role-workspace-list">
                              {selectedProgramPrepRows.map((row) => (
                                <article className="role-workspace-list-row" key={row.key}>
                                  <span className={`staff-pill ${row.signal === "Ready" || row.signal === "Roster started" ? "is-positive" : row.signal.startsWith("Needs") || row.signal === "Watch waitlist" ? "is-warning" : ""}`}>{row.type}</span>
                                  <div>
                                    <strong>{row.title}</strong>
                                    <small>{row.detail}</small>
                                  </div>
                                  <em>{row.signal}</em>
                                </article>
                              ))}
                            </div>
                          </section>
                          <aside className="role-workspace-side-panel">
                            <span className="section-kicker">Class</span>
                            <div className="role-workspace-preview-list">
                              <div><span>Roster</span><strong>{eventRegistrations.length}</strong></div>
                              <div><span>Waitlist</span><strong>{waitlistCount}</strong></div>
                              <div><span>Attendance left</span><strong>{needsAttendanceCount}</strong></div>
                            </div>
                          </aside>
                        </div>
                      )}

                      {activeSubtab === "roster" && (
                        <>
                          <div className="staff-roster-toolbar chapter-lead-roster-summary">
                            <div>
                              <strong>{eventRegistrations.length} people on the list</strong>
                              <span>{selectedRosterSummaryText}</span>
                            </div>
                          </div>
                          {eventRegistrationsLoading && <div className="empty-state">Loading roster...</div>}
                          {!eventRegistrationsLoading && eventRegistrations.length === 0 && (
                            <div className="empty-state compact-empty">
                              <strong>No registrations yet.</strong>
                            </div>
                          )}
                          <div className="staff-roster-list">
                            {eventRegistrations.map((registration) => {
                              const attendance = attendanceForRegistration(registration);
                              const personType = registration.memberId || registration.memberSnapshot?.memberId ? "Member" : "Walk-in";
                              return (
                                <article className="staff-roster-row chapter-lead-roster-row" key={registration.registrationId || registrationEmail(registration)}>
                                  <div className="staff-person-block">
                                    <strong>{registrationDisplayName(registration)}</strong>
                                    <span>{registrationEmail(registration) || "Email not set"}</span>
                                  </div>
                                  <div className="staff-status-block">
                                    <span>{personType}</span>
                                    <small>{attendance ? statusLabel(attendance.status) : "Attendance open"}</small>
                                  </div>
                                  <div className="row-actions compact-actions staff-roster-actions">
                                    <button
                                      type="button"
                                      disabled={recordingAttendanceKey === `${registration.registrationId}#attended`}
                                      onClick={() => recordAttendance(registration, "attended")}
                                    >
                                      Attended
                                    </button>
                                    <button
                                      type="button"
                                      disabled={recordingAttendanceKey === `${registration.registrationId}#no_show`}
                                      onClick={() => recordAttendance(registration, "no_show")}
                                    >
                                      No-show
                                    </button>
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {activeSubtab === "attendance" && (
                        <section className="staff-attendance-ledger chapter-lead-attendance-ledger">
                          <div className="panel-header">
                            <div>
                              <h3>Attendance</h3>
                            </div>
                          </div>
                          {eventAttendanceLoading && <div className="empty-state">Loading attendance...</div>}
                          {!eventAttendanceLoading && eventRegistrations.length === 0 && (
                            <div className="compact-status-row">No registrations are ready for attendance yet.</div>
                          )}
                          {!eventAttendanceLoading && eventRegistrations.map((registration) => {
                            const attendance = attendanceForRegistration(registration) || {};
                            const personType = registration.memberId || registration.memberSnapshot?.memberId ? "Member" : "Walk-in";
                            return (
                              <div className="staff-ledger-row staff-ledger-row-editable chapter-lead-attendance-row" key={registration.registrationId || registrationEmail(registration)}>
                                <span className={`staff-pill ${statusClass(attendance.status)}`}>{attendance.status ? statusLabel(attendance.status) : "Open"}</span>
                                <div>
                                  <strong>{registrationDisplayName(registration)}</strong>
                                  <small>{personType} / {registrationEmail(registration) || "Email not set"}</small>
                                </div>
                                <span>{attendance.checkedInAt || attendance.createdAt ? formatDateTime(attendance.checkedInAt || attendance.createdAt, selectedChapterTimezone) : "Not recorded"}</span>
                                <div className="row-actions compact-actions">
                                  {["attended", "no_show"].map((status) => (
                                    <button
                                      type="button"
                                      key={status}
                                      disabled={recordingAttendanceKey === `${registration.registrationId}#${status}`}
                                      onClick={() => recordAttendance(registration, status)}
                                    >
                                      {status === "no_show" ? "No-show" : statusLabel(status)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </section>
                      )}

                      {activeSubtab === "signoff" && (
                        <form className="staff-class-signoff chapter-lead-signoff-panel" onSubmit={submitClassSignoff}>
                          <div>
                            <span className="section-kicker">Signoff</span>
                            <h3>Close this class</h3>
                          </div>
                          <div className="staff-signoff-summary">
                            <div>
                              <span>Attendance recorded</span>
                              <strong>{eventAttendance.length}</strong>
	                            </div>
	                            <div>
	                              <span>Guest attendance</span>
	                              <strong>{manualAttendanceCount}</strong>
	                            </div>
                            <div>
                              <span>Still open</span>
                              <strong>{needsAttendanceCount}</strong>
                            </div>
                          </div>
                          {selectedEventSignoff ? (
                            <div className="staff-signoff-complete">
                              <strong>Signed off by {selectedEventSignoff.signedOffByName || "chapter lead"}</strong>
                              <span>{formatDateTime(selectedEventSignoff.signedOffAt, selectedChapterTimezone)}</span>
                              {selectedEventSignoff.note && <small>{selectedEventSignoff.note}</small>}
                            </div>
                          ) : (
                            <>
                              <label>
                                <span>Sign-off note</span>
                                <textarea
                                  rows={2}
                                  value={signoffNote}
                                  onChange={(event) => setSignoffNote(event.target.value)}
                                  placeholder="Optional"
                                />
                              </label>
                              <button
                                type="submit"
                                className="role-workspace-primary-action"
                                disabled={signoffDisabled}
                              >
                                {updatingEventId === selectedEvent?.eventId ? "Signing off..." : "Sign off class"}
                              </button>
                            </>
                          )}
                        </form>
                      )}
                    </>
                  )}
                </section>
              </section>
            )}
          </>
        )}

        {activeModuleId === "messages" && (
          <div className="role-workspace-dashboard-grid">
            {activeSubtab === "compose" && (
              <>
                <section className="role-workspace-compose-panel" aria-label="Compose scoped message">
                  <div>
                    <span className="section-kicker">Local message</span>
                    <h3>Compose update</h3>
                    <p>{messageBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <label>
                    <span>Audience</span>
                    <select
                      value={messageDraft.audience}
                      onChange={(event) => setMessageDraft((current) => ({ ...current, audience: event.target.value }))}
                    >
                      <option value="chapter">{selectedChapterLabel} members</option>
                      {selectedEvent && <option value="selected-program">{programDisplayTitle(selectedEvent)} roster</option>}
                      {upcomingPrograms.map((event) => (
                        <option key={event.eventId} value={event.eventId}>{programDisplayTitle(event)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Subject</span>
                    <input
                      type="text"
                      value={messageDraft.subject}
                      onChange={(event) => setMessageDraft((current) => ({ ...current, subject: event.target.value }))}
                      placeholder="Program reminder or chapter note"
                    />
                  </label>
                  <label>
                    <span>Message</span>
                    <textarea
                      rows={5}
                      value={messageDraft.body}
                      onChange={(event) => setMessageDraft((current) => ({ ...current, body: event.target.value }))}
                      placeholder="Write the note the chapter lead wants members to see."
                    />
                  </label>
                  <div className="role-workspace-panel-footer">
                    <span>Queueing and delivery review stay in the organization communication workflow.</span>
                    <button
                      type="button"
                      disabled={!messageDraft.subject.trim() || !messageDraft.body.trim()}
                      onClick={() => {
                        const nextDraft = {
                          ...messageDraft,
                          id: editingDraftId || `draft-${Date.now()}`,
                          savedAt: new Date().toISOString(),
                          context: selectedEvent ? programDisplayTitle(selectedEvent) : selectedChapterLabel
                        };
                        setSavedMessageDrafts((current) => [
                          nextDraft,
                          ...current.filter((draft) => draft.id !== nextDraft.id)
                        ].slice(0, 5));
                        setMessageDraft({ audience: selectedEvent ? "selected-program" : "chapter", subject: "", body: "" });
                        setEditingDraftId("");
                        switchSubtab("drafts");
                      }}
                    >
                      {editingDraftId ? "Update draft" : "Save draft"}
                    </button>
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Audience preview</span>
                  <h3>{selectedEvent ? programDisplayTitle(selectedEvent) : selectedChapterLabel}</h3>
                  <div className="role-workspace-preview-list">
                    <div><span>Likely audience</span><strong>{recipientCountLabel(selectedEvent ? eventRegistrations.length : openRosterCount, "Select a class to preview a roster audience")}</strong></div>
                    <div><span>Class context</span><strong>{selectedEvent ? programBrief(selectedEvent, visibleChapters, selectedChapterTimezone) : `${filteredEvents.length} visible classes`}</strong></div>
                    <div><span>Boundary</span><strong>Draft locally, review delivery separately</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "drafts" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Drafts</span>
                    <h3>Working drafts</h3>
                    <p>{messageBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {savedMessageDrafts.map((draft) => (
                      <article className={`role-workspace-list-row ${draft.status === "review_pending" ? "is-actionable" : ""}`} key={draft.id}>
                        <span className={`staff-pill ${draft.status === "review_pending" ? "is-warning" : "is-positive"}`}>
                          {draft.status === "review_pending" ? "Review pending" : "Saved"}
                        </span>
                        <div>
                          <strong>{draft.subject}</strong>
                          <small>{draft.body.slice(0, 140)}</small>
                          {draft.status === "review_pending" && (
                            <small>Owner review pending since {new Date(draft.submittedAt || draft.savedAt).toLocaleTimeString()}.</small>
                          )}
                        </div>
                        <small>{draft.context}</small>
                        <em>{new Date(draft.savedAt).toLocaleTimeString()}</em>
                        <button type="button" onClick={() => {
                          setMessageDraft({ audience: draft.audience, subject: draft.subject, body: draft.body });
                          setEditingDraftId(draft.id);
                          switchSubtab("compose");
                        }}>
                          Edit
                        </button>
                        <button type="button" onClick={() => setSavedMessageDrafts((current) => current.filter((item) => item.id !== draft.id))}>
                          Delete
                        </button>
                        {draft.status === "review_pending" ? (
                          <button type="button" disabled>
                            Awaiting owner review
                          </button>
                        ) : (
                          <button type="button" onClick={() => submitDraftForReview(draft.id)}>
                            Submit for review
                          </button>
                        )}
                      </article>
                    ))}
                    {savedMessageDrafts.length === 0 && upcomingPrograms.length === 0 ? (
                      <div className="compact-status-row">No upcoming programs need a local draft right now.</div>
                    ) : upcomingPrograms.slice(0, 3).map((event) => (
                      <article className="role-workspace-list-row" key={event.eventId}>
                        <span className="staff-pill is-warning">Draft idea</span>
                        <div>
                          <strong>{programDisplayTitle(event)} reminder</strong>
                          <small>{programBrief(event, visibleChapters, selectedChapterTimezone)}</small>
                        </div>
                        <button type="button" onClick={() => applyDraftIdea(event)}>
                          Use
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Draft health</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Program reminders</span><strong>{upcomingPrograms.length}</strong></div>
                    <div><span>Waitlist context</span><strong>{waitlistedRegistrations.length}</strong></div>
                    <div><span>Attendance follow-up</span><strong>{needsAttendanceCount}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "recent" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Recent context</span>
                    <h3>Recent message activity</h3>
                    <p>{messageBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {eventAttendance.slice(0, 4).map((entry, index) => (
                      <article className="role-workspace-list-row" key={entry.attendanceId || `${entry.email}-${index}`}>
                        <span className="staff-pill is-positive">Attendance</span>
                        <div>
                          <strong>{entry.fullName || entry.email || "Participant"}</strong>
                          <small>{statusLabel(entry.status)} / {formatDateTime(entry.checkedInAt || entry.createdAt, selectedChapterTimezone)}</small>
                        </div>
                      </article>
                    ))}
                    {eventAttendance.length === 0 && (
                      <div className="compact-status-row">No recent scoped activity is ready to summarize yet.</div>
                    )}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Delivery boundary</span>
                  <p>Message delivery issues, resend decisions, archive state, and provider settings stay in Notifications.</p>
                </aside>
              </>
            )}
            {activeSubtab === "templates" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Local templates</span>
                    <h3>Reusable chapter notes</h3>
                    <p>{messageBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-template-grid">
                    {["Program reminder", "Waitlist update", "Attendance follow-up"].map((template) => (
                      <button type="button" key={template} onClick={() => {
                        setMessageDraft({
                          audience: selectedEvent ? "selected-program" : "chapter",
                          subject: template,
                          body: selectedEvent
                            ? `${template} for ${programDisplayTitle(selectedEvent)}: please review the details and reply with any questions.`
                            : `${template} from ${selectedChapterLabel}: please review the details and reply with any questions.`
                        });
                        setEditingDraftId("");
                        switchSubtab("compose");
                      }}>
                        <strong>{template}</strong>
                        <span>Start a scoped draft</span>
                      </button>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Admin controls</span>
                  <p>Organization templates, sender rules, and delivery review stay with communication admins.</p>
                </aside>
              </>
            )}
          </div>
        )}

        {activeModuleId === "tasks" && (
          <div className="role-workspace-dashboard-grid">
            {activeSubtab === "open" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Open work</span>
                    <h3>Chapter lead task queue</h3>
                    <p>{taskBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {scopedTaskRows.length === 0 ? (
                      <div className="compact-status-row">No open chapter work needs action right now.</div>
                    ) : scopedTaskRows.map((row) => (
                      <article className="role-workspace-list-row" key={row.key}>
                        <span className="staff-pill is-warning">{row.type}</span>
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{row.due}</em>
                        <button type="button" onClick={() => openWorkspaceTask(row)}>{row.action}</button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Work mix</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Attendance left</span><strong>{needsAttendanceCount}</strong></div>
                    <div><span>Waitlist decisions</span><strong>{waitlistedRegistrations.length}</strong></div>
                    <div><span>Payment follow-up</span><strong>{paymentFollowUps.length}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "member-follow-ups" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Member follow-ups</span>
                    <h3>People needing a lead decision</h3>
                    <p>{taskBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {[...waitlistedRegistrations, ...paymentFollowUps].slice(0, 6).map((registration) => (
                      <article className="role-workspace-list-row" key={`${registration.registrationId}-follow-up`}>
                        <span className={`staff-pill ${statusClass(registration.paymentStatus || registration.status)}`}>{statusLabel(registration.paymentStatus || registration.status)}</span>
                        <div>
                          <strong>{registration.fullName || registration.email}</strong>
                          <small>{registration.email || "Email not set"}</small>
                        </div>
                        <button type="button" onClick={() => openWorkspaceTask({ moduleId: "programs", subtab: "roster" })}>Open Roster</button>
                      </article>
                    ))}
                    {waitlistedRegistrations.length === 0 && paymentFollowUps.length === 0 && (
                      <div className="compact-status-row">No member follow-ups are visible for the selected program.</div>
                    )}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Boundary</span>
                  <p>Member plan setup, billing policy, and global membership exports remain in the Membership admin area.</p>
                </aside>
              </>
            )}
            {activeSubtab === "program-prep" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Program prep</span>
                    <h3>Upcoming readiness</h3>
                    <p>{taskBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {upcomingPrograms.length === 0 ? (
                      <div className="compact-status-row">No upcoming programs are in this scope.</div>
                    ) : upcomingPrograms.map((event) => (
                      <article className="role-workspace-list-row" key={event.eventId}>
                        <span className={`staff-pill ${statusClass(event.status)}`}>{statusLabel(event.status)}</span>
                        <div>
                          <strong>{programDisplayTitle(event)}</strong>
                          <small>{programBrief(event, visibleChapters, selectedChapterTimezone)}</small>
                        </div>
                        <small>{eventRosterCount(event)}</small>
                        <button type="button" onClick={() => {
                          setSelectedEventId(event.eventId);
                          openWorkspaceTask({ moduleId: "programs", subtab: "prep" });
                        }}>
                          Open Prep
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Prep signals</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Upcoming</span><strong>{upcomingPrograms.length}</strong></div>
                    <div><span>Roster records</span><strong>{eventRegistrations.length}</strong></div>
                    <div><span>Scope</span><strong>{selectedChapterLabel}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "completed" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Completed</span>
                    <h3>Recent completed work</h3>
                    <p>{taskBoundaryCopy(activeSubtab)}</p>
                  </div>
                  <div className="role-workspace-list">
                    {completedTaskRows.length === 0 ? (
                      <div className="compact-status-row">No completed scoped work is visible yet.</div>
                    ) : completedTaskRows.map((row) => (
                      <article className="role-workspace-list-row" key={row.key}>
                        <span className="staff-pill is-positive">{row.type}</span>
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.detail}</small>
                        </div>
                        <em>{row.due}</em>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Handoff</span>
                  <p>Recent completions help another lead understand what already happened without exposing a raw audit log.</p>
                </aside>
              </>
            )}
          </div>
        )}

        {activeModuleId === "insights" && (
          <div className="role-workspace-dashboard-grid">
            {activeSubtab === "participation" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Participation</span>
                    <h3>Program engagement</h3>
                    <p>Use this to spot whether the selected program is converting registrations into attendance. Organization-wide reporting stays in Reports.</p>
                  </div>
                  <div className="role-workspace-insight-stack">
                    {insightBar("Roster", eventRegistrations.length, Math.max(eventRegistrations.length, 1), selectedRosterSummaryText, "is-positive")}
                    {insightBar("Attended", attendanceAttendedCount, Math.max(openRosterCount, attendanceTotalCount), `${attendanceAttendedCount} attendance records marked attended`, "is-positive")}
                    {insightBar("No-shows", attendanceNoShowCount, Math.max(openRosterCount, attendanceTotalCount), `${attendanceNoShowCount} people marked no-show`, attendanceNoShowCount > 0 ? "is-warning" : "")}
                    {insightBar("Excused", attendanceExcusedCount, Math.max(openRosterCount, attendanceTotalCount), `${attendanceExcusedCount} excused attendance records`)}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Signal</span>
                  <div className="role-workspace-preview-list">
                    {insightPercentCard("Attendance completion", attendanceTotalCount, Math.max(openRosterCount, attendanceTotalCount), `${needsAttendanceCount} still open`)}
                    {insightPercentCard("Attendance recorded", attendanceTotalCount, Math.max(openRosterCount, attendanceTotalCount), selectedRosterSummaryText)}
                    <div><span>Selected class</span><strong>{selectedEvent ? programDisplayTitle(selectedEvent) : "Select a class"}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "growth" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Growth</span>
                    <h3>Chapter member movement</h3>
                    <p>Growth keeps the chapter lead focused on local membership movement and waitlist pressure. Pricing, renewals, and global exports stay in Membership and Reports.</p>
                  </div>
                  <div className="role-workspace-insight-stack">
                    {insightBar("Active members", activeScopedMembers.length, scopedMembers.length, `${activeScopedMembers.length} active of ${scopedMembers.length} visible members`, "is-positive")}
                    {insightBar("Recent joins", recentMemberJoins.length, Math.max(scopedMembers.length, 1), "Joined in the last 30 days", "is-positive")}
                    {insightBar("Waitlist pressure", waitlistCount, Math.max(eventRegistrations.length, waitlistCount), `${waitlistCount} people waiting on selected program`, waitlistCount > 0 ? "is-warning" : "")}
                    {insightBar("Needs review", membersNeedingReview.length, Math.max(scopedMembers.length, membersNeedingReview.length), `${membersNeedingReview.length} members need a lead decision`, membersNeedingReview.length > 0 ? "is-warning" : "")}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Local picture</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Scope</span><strong>{selectedChapterLabel}</strong></div>
                    <div><span>Visible members</span><strong>{scopedMembers.length}</strong></div>
                    <div><span>Visible programs</span><strong>{filteredEvents.length}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "readiness" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Readiness</span>
                    <h3>Work before the next handoff</h3>
                    <p>Readiness combines member requirements, review pressure, attendance gaps, and payment follow-up into a lead-facing action picture.</p>
                  </div>
                  <div className="role-workspace-insight-stack">
                    {insightBar("Requirements open", membersWithRequirements.length, readinessPressure, `${membersWithRequirements.length} members need requirement follow-up`, membersWithRequirements.length > 0 ? "is-warning" : "is-positive")}
                    {insightBar("Review pressure", membersNeedingReview.length, readinessPressure, `${membersNeedingReview.length} member decisions`, membersNeedingReview.length > 0 ? "is-warning" : "is-positive")}
                    {insightBar("Attendance gaps", needsAttendanceCount, readinessPressure, `${needsAttendanceCount} attendance records still open`, needsAttendanceCount > 0 ? "is-warning" : "is-positive")}
                    {insightBar("Payment follow-up", paymentFollowUps.length, readinessPressure, `${paymentFollowUps.length} roster payment follow-ups`, paymentFollowUps.length > 0 ? "is-warning" : "is-positive")}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Next best action</span>
                  <h3>{readinessPressure > 0 ? "Work the open queue" : "Chapter looks ready"}</h3>
                  <p>{readinessPressure > 0 ? "Open Tasks to work the highest-pressure items in this scope." : "No urgent member or program readiness signals are visible in this scope."}</p>
                  {readinessPressure > 0 && (
                    <button type="button" className="secondary-button" onClick={() => openWorkspaceTask({ moduleId: "tasks", subtab: "open" })}>
                      Open Tasks
                    </button>
                  )}
                </aside>
              </>
            )}
            {activeSubtab === "communication" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Communication</span>
                    <h3>Who needs a message</h3>
                    <p>This is message intelligence for the chapter lead. Delivery settings, resend triage, and provider setup stay in Communications and Notifications.</p>
                  </div>
                  <div className="role-workspace-insight-stack">
                    {insightBar("Class audience", selectedEvent ? eventRegistrations.length : openRosterCount, Math.max(eventRegistrations.length, openRosterCount), selectedEvent ? `${programDisplayTitle(selectedEvent)} roster` : "Select a class to preview roster audience", "is-positive")}
                    {insightBar("Waitlist update", waitlistCount, Math.max(eventRegistrations.length, waitlistCount), `${waitlistCount} people may need a waitlist update`, waitlistCount > 0 ? "is-warning" : "")}
                    {insightBar("Attendance follow-up", needsAttendanceCount, Math.max(openRosterCount, needsAttendanceCount), `${needsAttendanceCount} people without attendance outcome`, needsAttendanceCount > 0 ? "is-warning" : "")}
                    {insightBar("Local templates", 3, 3, "Reminder, waitlist update, and attendance follow-up starters")}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Compose from insight</span>
                  <h3>{selectedEvent ? programDisplayTitle(selectedEvent) : selectedChapterLabel}</h3>
                  <p>Start a local draft from the audience and follow-up signals. Communication review controls final delivery.</p>
                  <button type="button" className="secondary-button" onClick={() => {
                    openWorkspaceTask({ moduleId: "messages", subtab: "compose" });
                  }}>
                    Compose Message
                  </button>
                </aside>
              </>
            )}
          </div>
        )}

        {activeModuleId === "settings" && (
          <div className="role-workspace-dashboard-grid">
            {activeSubtab === "profile" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Local profile</span>
                    <h3>{selectedSettingsChapter?.name || selectedChapterLabel}</h3>
                    <p>Chapter leads can review the local profile that shapes member context. Organization-wide chapter setup and public directory publishing stay in Chapters.</p>
                  </div>
                  <div className="role-workspace-list">
                    {chapterReadinessRows.map((row) => (
                      <article className="role-workspace-list-row" key={row.label}>
                        <span className="staff-pill">{row.label}</span>
                        <div>
                          <strong>{row.value}</strong>
                          <small>{row.detail}</small>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Directory state</span>
                  <div className="role-workspace-preview-list">
                    <div><span>Public directory</span><strong>{chapterPublicState(selectedSettingsChapter || {})}</strong></div>
                    <div><span>Timezone</span><strong>{selectedChapterTimezone}</strong></div>
                    <div><span>Scope</span><strong>{selectedChapterLabel}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "lead-team" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Lead team</span>
                    <h3>Contact coverage</h3>
                    <p>This tab shows visible local contacts only. Inviting people, changing roles, and expanding chapter scope stay in Access.</p>
                  </div>
                  <div className="role-workspace-list">
                    {chapterLeaders(selectedSettingsChapter || {}).length === 0 ? (
                      <div className="compact-status-row">No local lead contact is listed for this chapter.</div>
                    ) : chapterLeaders(selectedSettingsChapter || {}).map((leader) => (
                      <article className="role-workspace-list-row" key={leader}>
                        <span className="staff-pill is-positive">Listed</span>
                        <div>
                          <strong>{leader}</strong>
                          <small>{selectedSettingsChapter?.name || selectedChapterLabel}</small>
                        </div>
                        <em>Contact coverage</em>
                      </article>
                    ))}
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Access boundary</span>
                  <p>Lead contacts are not the same as sign-in access. Admins grant workspace roles and application scope from Access.</p>
                  <div className="role-workspace-preview-list">
                    <div><span>Listed contacts</span><strong>{chapterLeaders(selectedSettingsChapter || {}).length}</strong></div>
                    <div><span>Visible chapters</span><strong>{visibleChapters.length}</strong></div>
                  </div>
                </aside>
              </>
            )}
            {activeSubtab === "defaults" && (
              <>
                <section className="role-workspace-list-panel">
                  <div>
                    <span className="section-kicker">Local defaults</span>
                    <h3>Chapter operating defaults</h3>
                    <p>Defaults summarize the local context a lead needs before drafting messages or prepping programs. Organization templates and app configuration stay admin-controlled.</p>
                  </div>
                  <div className="role-workspace-list">
                    <article className="role-workspace-list-row">
                      <span className="staff-pill">Programs</span>
                      <div>
                        <strong>{filteredEvents.length} visible programs</strong>
                        <small>{upcomingPrograms.length} upcoming inside this scope</small>
                      </div>
                    </article>
                    <article className="role-workspace-list-row">
                      <span className="staff-pill">Messages</span>
                      <div>
                        <strong>{selectedSettingsChapter?.contactEmail || "Use organization sender defaults"}</strong>
                        <small>Final delivery settings stay in Communications and Notifications.</small>
                      </div>
                    </article>
                    <article className="role-workspace-list-row">
                      <span className="staff-pill">Readiness</span>
                      <div>
                        <strong>{readinessPressure > 0 ? `${readinessPressure} open signals` : "No urgent signals"}</strong>
                        <small>Tasks remains the place to work readiness follow-up.</small>
                      </div>
                    </article>
                  </div>
                </section>
                <aside className="role-workspace-side-panel">
                  <span className="section-kicker">Admin controls</span>
                  <p>Chapter setup, public visibility, templates, sender rules, and application configuration stay in admin tools.</p>
                </aside>
              </>
            )}
          </div>
        )}

        {["range-board", "shooters", "safety", "equipment", "shift-notes"].includes(activeModuleId) && operatorModulePanel(
          activeModuleId,
          activeSubtab,
          [
            { label: "Assigned scope", value: selectedChapterLabel },
            { label: "Active area", value: activeModuleConfig.label }
          ]
        )}

        {["issue-desk", "service", "inventory", "notes"].includes(activeModuleId) && operatorModulePanel(
          activeModuleId,
          activeSubtab,
          [
            { label: "Assigned scope", value: selectedChapterLabel },
            { label: "Active area", value: activeModuleConfig.label }
          ]
        )}
      </section>
    </div>
  );
}
